// ModalParcela — modal grande com 5 abas (Detalhes, Dados pra NF, Pagamento,
// Anexos, Histórico). Reforma de 2026-09-01 (pedido do Lucas): largura da
// regra da casa (min(94vw, 1360px)), Detalhes em 2 colunas, campo ÚNICO de
// título (regra título×descrição em parcelaForm.ts — a descrição detalhada só
// aparece quando as colunas divergem de verdade: aditivo/CA), corpo da NF só
// na aba Dados pra NF, e-mail de faturamento aposentado da tela (a lista de
// destinatários da NF, que morava na aba própria, foi pra Dados pra NF).
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Tabs, Select } from 'antd';
import { API_URL } from '../../config';
import { SelectCliente } from '../../components/SelectCliente';
import { hojeIso } from '../../utils/dataFmt';
import { getToken } from '../../utils/auth';
import { fmt, fmtData, num, STATUS_PARC, inputMP, SETORES } from './utils';
import { CNAES, rotuloCnae } from '../../utils/cnaes';
import {
  semearTituloDescricao, montarPayloadTituloDescricao,
  semearDestinatarios, montarPayloadDestinatarios, incluirAvulsos,
  montarPayloadRateio, camposAlterados, semearRateioPct,
} from './parcelaForm';
import type { ModoTituloDesc, Destinatario } from './parcelaForm';
import type { Parcela, OfxVinculada } from './types';
import { CampoMP } from './CampoMP';
import { RateioParcela } from './RateioParcela';
import { AnexosParcela } from './AnexosParcela';
import { EmailsNfParcela } from './EmailsNfParcela';
import { HistoricoParcela } from './HistoricoParcela';
import { ModalMesclarParcelaCr } from './ModalMesclarParcelaCr';

export function ModalParcela({ parcela, onClose, onMudou, abaInicial }: {
  parcela: Parcela;
  onClose: () => void;
  onMudou: () => void;
  // 2026-09-02: aberto pelo sino de menção → cai direto na aba Histórico.
  abaInicial?: 'detalhes' | 'nf' | 'pagamento' | 'anexos' | 'historico';
}) {
  type Aba = 'detalhes' | 'nf' | 'pagamento' | 'anexos' | 'historico';
  const [aba, setAba] = useState<Aba>(abaInicial || 'detalhes');
  const [salvando, setSalvando] = useState(false);
  const navigate = useNavigate();
  // Onda 0 (2026-07-02): handoff pro Extrato via URL (link copiável) — substitui
  // o sessionStorage['ofx_busca_pendente'] + CustomEvent app-navigate.
  function irParaExtrato(valorAlvo: number) {
    if (!confirmarDescartar('Sair daqui para o extrato')) return;
    const qs = new URLSearchParams({
      q: parcela.cliente_nome || '',
      valor: String(valorAlvo),
      parcela: parcela.id,
      status: 'nao_conciliadas',
    });
    navigate(`/financeiro/extrato-bancario?${qs.toString()}`);
    onClose();
  }
  // F-CR-merge: estado pro modal de mesclar com parcela existente
  const [mostrarMerge, setMostrarMerge] = useState(false);
  // OFX vinculadas via conciliacao bancaria (legacy parcela_id OU multi N:M)
  const [ofxVinculadas, setOfxVinculadas] = useState<OfxVinculada[]>([]);
  useEffect(() => {
    if (!parcela.qtd_ofx_vinculadas) { setOfxVinculadas([]); return; }
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'fin_listar_ofx_da_parcela', parcela_id: parcela.id },
          { headers: { Authorization: token } });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        setOfxVinculadas(Array.isArray(d) ? d : []);
      } catch { /* silencia — modal funciona sem essa lista */ }
    })();
  }, [parcela.id, parcela.qtd_ofx_vinculadas]);

  // Hidratação (2026-07-08): a listagem do CR omite campos pesados
  // (rateio_setores, impostos, observacoes...) pra não estourar o payload.
  // Busca o registro completo ao abrir — sem isso o rateio existente
  // aparecia como "sem rateio" e salvar podia apagar observações.
  const [detalhe, setDetalhe] = useState<Partial<Parcela> | null>(null);
  // Save-gate (2026-09-01): a LISTAGEM trunca descricao/parcela_titulo em 200
  // caracteres; salvar antes de o detalhe chegar gravaria o corte (a BM 15 do
  // 25010 perdeu 10 caracteres assim em 20/08/2026). Enquanto 'carregando', o
  // botão Salvar fica travado; 'falhou' reabilita com aviso âmbar + tentar de
  // novo — falha de rede não pode trancar o modal pra sempre.
  const [detalheStatus, setDetalheStatus] = useState<'carregando' | 'ok' | 'falhou'>('carregando');
  // O status de AGORA para quem roda depois (a recarga chamada por outro card).
  const detalheStatusRef = useRef(detalheStatus);
  detalheStatusRef.current = detalheStatus;
  async function carregarDetalhe(inicial = false) {
    if (inicial) setDetalheStatus('carregando');
    // Recarga pedida por outro card (rateio, entradas) com a carga inicial
    // tendo FALHADO: se ela só promovesse o status a 'ok', a "porta do falhou"
    // do salvar desligava com o form ainda vazio — e o Salvar apagava
    // observações, corpo da NF e prazo (revisão adversarial de 21/09, mesmo
    // mecanismo de 21/08). Então ela hidrata como a inicial (idempotente).
    const hidratar = inicial || detalheStatusRef.current !== 'ok';
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'fin_obter_conta_receber', id: parcela.id },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d?.id) {
        setDetalhe(d);
        // Destinatários da NF: semeados SÓ pelo detalhe (a listagem não traz a
        // lista) e só enquanto a pessoa não mexeu — as recargas feitas por
        // outros cards (rateio, entradas) não podem atropelar a seleção dela.
        if (!mexeuDestinatarios.current) {
          const sd = semearDestinatarios(d.emails_nf_destinatarios, d.email_faturamento);
          setDestinatarios(sd.lista);
          setDestSugerido(sd.sugerido);
          // Só se esvazia o campo antigo quando TUDO dele virou destinatário;
          // sobrando pedaço que não parece e-mail, ele fica guardado e à vista.
          legadoOferecido.current = sd.sugerido && sd.naoLidos.length === 0;
          setDestLegado(sd.sugerido || sd.naoLidos.length
            ? { texto: String(d.email_faturamento || ''), naoLidos: sd.naoLidos } : null);
          setDestSalvos(JSON.stringify(sd.sugerido ? [] : sd.lista));
        }
        // Hidrata campos omitidos da listagem (descricao_nf só vem no detalhe).
        // 🚨 Campo do form que a listagem NÃO traz e que NÃO for hidratado aqui
        // abre vazio e é APAGADO no primeiro salvar — o payload manda o form
        // inteiro. Foi o que aconteceu (Lucas, 2026-08-21): 55 parcelas
        // perderam o e-mail de faturamento e 89 perderam o prazo entre 23/06 e
        // 21/08/2026. Antes de somar campo novo ao form, confira se ele está
        // no SELECT de finListarContasReceber.
        if (hidratar) {
          // 🚨 A BASE é calculada a partir dela mesma (que nasce do estado
          // inicial), NUNCA a partir do `f` corrente — os campos ficam
          // editáveis enquanto o detalhe carrega, e capturar `f` depois faria
          // o que a pessoa digitou nesse meio-tempo virar "valor original".
          // Com o payload mandando só o que mudou (30/09), isso não seria só
          // um aviso a menos: a edição sumiria sem ser gravada. Mesma conta
          // que a hidratação do form, com a base no lugar do `f`.
          const base = formBase.current;
          formBase.current = {
            ...base,
            observacoes: base.observacoes || d.observacoes || '',
            descricao_nf: base.descricao_nf || d.descricao_nf || '',
            cnae: base.cnae || d.cnae || '',
            prazo_dias: base.prazo_dias ?? d.prazo_dias ?? null,
          };
          setForm(f => ({
            ...f,
            // f.x || d.x: o "tentar de novo" re-roda esta hidratação — sem a
            // guarda, o clique apagava o que a pessoa já tinha digitado
            // (revisão adversarial de 01/09; observacoes era o furo).
            observacoes: f.observacoes || d.observacoes || '',
            descricao_nf: f.descricao_nf || d.descricao_nf || '',
            cnae: f.cnae || d.cnae || '',
            prazo_dias: f.prazo_dias ?? d.prazo_dias ?? null,
          }));
          // Título/descrição re-semeados do DETALHE (texto inteiro, não o
          // truncado da listagem) — a regra única mora em parcelaForm.ts.
          // A SEMENTE entra na base sempre; no form, só enquanto a pessoa não
          // digitou nada (mexeuTituloDesc).
          const s = semearTituloDescricao({
            parcela_titulo: d.parcela_titulo ?? parcela.parcela_titulo,
            descricao: d.descricao ?? parcela.descricao,
          });
          formBase.current = {
            ...formBase.current, parcela_titulo: s.titulo, descricao: s.descricao,
          };
          if (!mexeuTituloDesc.current) {
            setModoTituloDesc(s.modo);
            haviaTextoTitulo.current = !!(s.titulo || s.descricao);
            setForm(f => ({ ...f, parcela_titulo: s.titulo, descricao: s.descricao }));
          }
        }
        setDetalheStatus('ok');
      } else if (inicial) {
        // Refresh não-inicial que falha NÃO rebaixa o status: o form já foi
        // hidratado pela carga inicial — rebaixar dispararia o aviso falso e
        // o retry destrutivo (revisão adversarial de 01/09).
        setDetalheStatus('falhou');
      }
    } catch {
      // modal segue com os dados da listagem, mas o Salvar avisa (texto longo
      // pode ser salvo cortado) em vez de gravar o corte em silêncio
      if (inicial) setDetalheStatus('falhou');
    }
  }
  useEffect(() => {
    carregarDetalhe(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parcela.id]);

  // Entradas da parcela (Lucas 2026-07-08): recebimentos-filho que compõem o
  // recebido efetivo (ex.: BM da Profill agrega N pagamentos). O usuário
  // analisa aqui e registra entradas manuais vinculadas à parcela prevista.
  type Entrada = {
    id: string; descricao: string | null; valor_recebido: number;
    valor_previsto?: number; status?: string;
    data_recebimento: string | null; data_competencia: string | null;
    origem: string; id_externo_ca: string | null; entrada_manual: boolean;
    qtd_ofx: number; soma_ofx: number; meio_pagamento: string | null;
    // FZ 5.2 — conta bancária da entrada (backend em rollout paralelo)
    conta_financeira_id?: string | null; conta_financeira_apelido?: string | null;
  };
  const [entradas, setEntradas] = useState<Entrada[]>([]);
  const [novaEntrada, setNovaEntrada] = useState({
    aberto: false, valor: '' as string | number, data: hojeIso(), descricao: '',
    conta_financeira_id: '',
  });
  // Cadastro oficial de contas — a lista crua vem numa busca só (contasFin,
  // mais abaixo; antes eram DUAS chamadas idênticas a /financeiro/contas em
  // estados separados). A entrada SEM conta fica invisível no fluxo por conta
  // (caso Daniele/Marina 2026-08-10: 5 entradas com conta NULL) — os selects
  // de ENTRADA usam só as ativas; o select da PARCELA usa a crua, pra conta
  // inativa já vinculada continuar visível.
  async function carregarEntradas() {
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'fin_listar_entradas_parcela', parcela_id: parcela.id },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setEntradas(Array.isArray(d?.entradas) ? d.entradas : []);
    } catch { /* silencia — modal funciona sem a lista */ }
  }
  useEffect(() => {
    carregarEntradas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parcela.id]);

  async function registrarEntrada() {
    const valor = Number(novaEntrada.valor);
    if (!(valor > 0)) { alert('Informe o valor da entrada'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`, {
        action: 'fin_registrar_entrada_parcela',
        parcela_id: parcela.id,
        valor,
        data_recebimento: novaEntrada.data,
        descricao: novaEntrada.descricao || undefined,
        conta_financeira_id: novaEntrada.conta_financeira_id || undefined,
      }, { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d?.erro) { alert(d.erro); return; }
      setNovaEntrada({ aberto: false, valor: '', data: hojeIso(), descricao: '', conta_financeira_id: '' });
      await carregarEntradas();
      onMudou();
    } catch (e: any) {
      alert('Erro ao registrar entrada: ' + (e?.response?.data?.error || e?.response?.data?.erro || e.message));
    } finally { setSalvando(false); }
  }

  async function excluirEntrada(entradaId: string) {
    if (!window.confirm('Excluir esta entrada manual? O status da parcela será recalculado.')) return;
    setSalvando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'fin_excluir_entrada_parcela', entrada_id: entradaId },
        { headers: { Authorization: token } });
      await carregarEntradas();
      onMudou();
    } catch (e: any) {
      alert('Erro ao excluir entrada: ' + (e?.response?.data?.error || e?.response?.data?.erro || e.message));
    } finally { setSalvando(false); }
  }
  // Editar o valor de uma entrada já registrada — inclusive espelho do Conta
  // Azul. Sem isso, corrigir um valor que mudou no CA só dava criando OUTRA
  // entrada, e o valor entrava duas vezes (caso Airbnb 03/07). O backend
  // acerta junto a baixa do CA, senão o fluxo de caixa ignoraria a edição.
  const [editandoEntrada, setEditandoEntrada] =
    useState<{ id: string; valor: string; data: string; conta?: string } | null>(null);

  async function salvarEntradaEditada() {
    if (!editandoEntrada) return;
    const valor = Number(String(editandoEntrada.valor).replace(',', '.'));
    if (!Number.isFinite(valor) || valor < 0) { alert('Valor inválido'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'fin_editar_entrada_parcela', entrada_id: editandoEntrada.id,
          conta_financeira_id: editandoEntrada.conta ?? undefined,
          valor_recebido: valor,
          // Data também: pagamento de terceiro entra e sai no mesmo dia, e
          // quando as duas pernas ficam em dias diferentes o caixa desencontra
          // (casos 15/07 e 20/07).
          data_recebimento: editandoEntrada.data || undefined },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d?.erro) { alert(d.erro); return; }
      setEditandoEntrada(null);
      await carregarEntradas();
      onMudou();
    } catch (e: any) {
      alert('Erro ao editar entrada: ' + (e?.response?.data?.error || e?.response?.data?.erro || e.message));
    } finally { setSalvando(false); }
  }

  const totalEntradas = entradas.reduce((s, e) => s + num(e.valor_recebido), 0);

  // Título × Descrição (2026-09-01): UM campo na tela; a "Descrição detalhada"
  // só existe quando as colunas divergem de verdade (aditivo: descricao =
  // motivo; parcela CA re-sobrescrita pelo sync). A regra mora em
  // parcelaForm.ts; o payload SEMPRE grava as duas colunas (o e-mail "NF a
  // emitir" lê descricao crua). O modo é decidido na abertura/hidratação e
  // nunca ao vivo dos valores — senão o segundo campo pisca durante a
  // digitação. mexeuTituloDesc impede a hidratação de atropelar o que a
  // pessoa já digitou.
  const semente0 = useMemo(() => semearTituloDescricao(parcela),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parcela.id]);
  const [modoTituloDesc, setModoTituloDesc] = useState<ModoTituloDesc>(semente0.modo);
  const mexeuTituloDesc = useRef(false);
  // A parcela TINHA texto? Título esvaziado com haviaTexto = nada é gravado
  // (o banco preserva) — sem isso, selecionar-tudo-e-apagar zerava também a
  // descricao, que os e-mails automáticos leem crua.
  const haviaTextoTitulo = useRef(!!(semente0.titulo || semente0.descricao));
  // Campos que SÓ o detalhe hidrata: se a pessoa os mexeu, o valor dela vale;
  // se NÃO mexeu e o detalhe não chegou ('falhou'), eles saem do payload no
  // salvar — senão o form vazio apagava observações/corpo da NF/prazo no
  // banco (o mesmo mecanismo do incidente de 21/08, pela porta do 'falhou').
  const camposMexidos = useRef<Set<string>>(new Set());
  // Digitou no título com o detalhe ainda carregando/falho: a base pode ser o
  // texto TRUNCADO da listagem (200 chars) — avisa em vez de calar.
  const [edicaoAntecipada, setEdicaoAntecipada] = useState(false);
  // Destinatários da NF (21/09/2026, Lucas: "eu não deveria precisar salvar
  // destinatários dentro do modal que também é salvo"). A lista mora AQUI, fora
  // do `form` — o form vai inteiro no payload e a listagem não traz a lista, só
  // o detalhe. Ela vai no MESMO Salvar alterações, mas só se o detalhe carregou
  // e a pessoa mexeu (montarPayloadDestinatarios, parcelaForm.ts). E mora aqui,
  // não no card, também porque a aba NF desmonta ao trocar de aba — a seleção
  // não salva morria sem aviso.
  const [destinatarios, setDestinatarios] = useState<Destinatario[]>([]);
  const [destSugerido, setDestSugerido] = useState(false);
  const [destSalvos, setDestSalvos] = useState('[]');
  const [destMexeu, setDestMexeu] = useState(false);
  const [destLegado, setDestLegado] = useState<{ texto: string; naoLidos: string[] } | null>(null);
  // Texto no campo "digite/cole e-mail(s)" que ainda não entrou na lista. Mora
  // aqui para sobreviver à troca de aba e para o Salvar enxergar.
  const [destDigitado, setDestDigitado] = useState('');
  // Contatos que o card leu — o Salvar inclui o texto pendente com a MESMA
  // régua do Enter (e-mail de contato vira o contato).
  const contatosNfRef = useRef<any[]>([]);
  const mexeuDestinatarios = useRef(false);
  // A lista veio sugerida do campo antigo email_faturamento nesta abertura?
  const legadoOferecido = useRef(false);
  const destAlterado = destMexeu && JSON.stringify(destinatarios) !== destSalvos;
  // Rateio por setor em edição. Mora AQUI (não no card) desde 30/09/2026: o
  // card tinha Salvar próprio, então editar o rateio e salvar pelo rodapé —
  // ou o contrário — jogava metade fora. null = ninguém está editando.
  const [rateioPct, setRateioPct] = useState<Record<string, string> | null>(null);
  // O formulário como o SISTEMA o teria sem ninguém digitar: nasce do estado
  // inicial (a linha da listagem) e recebe a hidratação do detalhe e os
  // ajustes automáticos. É contra ele que se pergunta "o que a pessoa mexeu?"
  // — tanto para avisar ao fechar quanto para montar o payload.
  // (semeado logo abaixo, junto do useState do form.)
  const formBase = useRef<Record<string, any>>({});
  const baseSemeada = useRef(false);
  function mudarDestinatarios(lista: Destinatario[]) {
    mexeuDestinatarios.current = true;
    setDestMexeu(true);
    setDestSugerido(false);   // mexeu = decidiu; o que ficou na lista vale
    setDestinatarios(lista);
  }
  // Fundo, × e Fechar: QUALQUER coisa mexida e não salva pergunta antes de
  // sumir (30/09/2026 — antes só os destinatários perguntavam; o resto do
  // formulário e o rateio iam embora calados).
  function confirmarDescartar(oQueVaiFazer: string) {
    if (!pendencias.length) return true;
    return window.confirm(
      `Você mexeu ${pendencias.join(', ')} e ainda não salvou.\n\n`
      + `${oQueVaiFazer} assim mesmo e descartar?`);
  }
  function fecharModal() {
    if (!confirmarDescartar('Fechar')) return;
    onClose();
  }

  // Estado editável (espelha parcela; salva via PATCH)
  const [form, setForm] = useState({
    // Sprint F15 — contrato_id editável (re-vincular parcela a aditivo, p.ex.)
    contrato_id: parcela.contrato_id || '',
    // F-CR-contrato-externo: rotulo livre quando contrato nao esta no operacional
    contrato_externo: parcela.contrato_externo || '',
    // F-CR-cliente: cliente_id editavel pra parcelas CA orfas sem cliente cadastrado
    cliente_id: parcela.cliente_id ? String(parcela.cliente_id) : '',
    parcela_titulo: semente0.titulo,
    descricao: semente0.descricao,
    valor_previsto: parcela.valor_previsto,
    data_vencimento: parcela.data_vencimento?.slice(0, 10) || '',
    observacoes: parcela.observacoes || '',
    status: parcela.status,
    meio_pagamento: parcela.meio_pagamento || '',
    nota_fiscal_numero: parcela.nota_fiscal_numero || '',
    // Sprint F8 — campos novos editáveis
    // (email_faturamento saiu do form em 2026-09-01: a coluna virou legado —
    // quem manda é a lista de destinatários da NF, na aba Dados pra NF. Fora
    // do form = fora do payload = nunca mais escrita nem apagada.)
    prazo_dias: parcela.prazo_dias ?? null as number | null,
    // Sprint F8.3 — emissão da NF (vencimento = emissão + prazo)
    nota_fiscal_emissao: parcela.nota_fiscal_emissao?.slice(0, 10) || '',
    // F-CR-tipo: bucket pra filtro Diretor vs Marina
    tipo_receita: parcela.tipo_receita || null as string | null,
    // FIN-7ajustes (2026-07-22): competência editável, desconto livre + motivo,
    // descrição do corpo da NF (auto/editável).
    data_competencia: parcela.data_competencia?.slice(0, 10) || '',
    descontos: parcela.descontos ?? 0 as number,
    descontos_motivo: parcela.descontos_motivo || '',
    descricao_nf: parcela.descricao_nf || '',
    // NF-CNAE: código de serviço da NF (editável; herda do serviço-fim do contrato)
    cnae: parcela.cnae || '',
    // FZ 5.2: conta bancária da parcela (fluxo por banco) — a conciliação OFX
    // carimba sozinha; aqui é o ajuste manual / conta esperada do previsto.
    conta_financeira_id: (parcela as any).conta_financeira_id || '',
  });
  // Semeia a base com o formulário como ele NASCEU (a linha da listagem).
  // Sem isso, mexer no valor ou no vencimento antes de o detalhe chegar não
  // contava como alteração — e, com o payload parcial, nem seria salvo.
  if (!baseSemeada.current) { baseSemeada.current = true; formBase.current = { ...form }; }
  // FZ 5.2 — contas cadastradas (lista crua, busca ÚNICA — ver comentário do
  // cadastro de contas acima). Ativas derivadas pra os selects de entrada.
  const [contasFin, setContasFin] = useState<any[]>([]);
  const contasFinanceiras = useMemo(
    () => contasFin.filter((c: any) => c.ativo !== false), [contasFin]);
  // Sprint F15 — lista de contratos disponíveis pro dropdown de re-vincular.
  // Inclui aditivos (filhos) pra permitir mover parcela do pai pro aditivo.
  const [contratosLista, setContratosLista] = useState<Array<{
    id: string; numero: string; titulo: string; contrato_pai_id?: string | null;
    cliente_id?: number; cliente_nome?: string;
  }>>([]);
  // A lista de clientes saiu daqui: o SelectCliente carrega a sua com cache de
  // 10 min compartilhado. Este modal fazia um GET /comercial/clientes?limit=2000
  // toda vez que abria.
  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const rContratos = await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'op_listar_contratos', incluir_aditivos: true },
          { headers: { Authorization: token } });
        const dC = rContratos.data.body ? JSON.parse(rContratos.data.body) : rContratos.data;
        setContratosLista(Array.isArray(dC) ? dC : []);
      } catch { /* dropdown vazio se falhar — não bloqueia o modal */ }
      // FZ 5.2 — contas cadastradas (best-effort, fora do Promise.all principal)
      try {
        const token = await getToken();
        // incluir_inativas: a lista crua abaixo existe para a conta inativa já
        // vinculada continuar visível — mas o GET só devolvia as ativas.
        const rCF = await axios.get(`${API_URL}/financeiro/contas?incluir_inativas=1`,
          { headers: { Authorization: token } });
        const dCF = rCF.data.body ? JSON.parse(rCF.data.body) : rCF.data;
        // Aceita os dois formatos ({contas:[...]} ou lista crua) — era o que
        // as duas buscas antigas cobriam separadamente.
        setContasFin(Array.isArray(dCF) ? dCF : (dCF?.contas || []));
      } catch { /* select de conta fica vazio */ }
    })();
  }, []);

  // Multi-CNPJ (2026-08-04): AGRUPA em vez de filtrar.
  // O filtro por cliente resolvia um problema real (achar o contrato certo
  // entre centenas), mas tornava o caso multi-CNPJ impossível: ao apontar a
  // parcela pra outro CNPJ, o contrato dela sumia da lista e não dava pra
  // reselecionar. Agora os contratos do cliente vêm primeiro e o resto fica
  // acessível — e o contrato JÁ VINCULADO nunca sai da lista, aconteça o que
  // acontecer com o cliente.
  const gruposContrato = useMemo(() => {
    const ordena = (a: any, b: any) => {
      if (!a.contrato_pai_id && b.contrato_pai_id) return -1;
      if (a.contrato_pai_id && !b.contrato_pai_id) return 1;
      return String(a.numero).localeCompare(String(b.numero));
    };
    const rotulo = (c: any) => (c.contrato_pai_id ? '└─ ' : '')
      + c.numero + (c.titulo ? ` · ${String(c.titulo).slice(0, 60)}` : '');
    const cliId = form.cliente_id ? parseInt(form.cliente_id) : null;
    const doCliente = cliId ? contratosLista.filter(c => c.cliente_id === cliId) : [];
    const idsDoCliente = new Set(doCliente.map(c => c.id));
    const outros = contratosLista.filter(c => !idsDoCliente.has(c.id));
    const grupos: Array<{ label: string; options: Array<{ value: string; label: string }> }> = [];

    // O contrato atual entra sempre — é a garantia de que trocar o CNPJ não
    // deixa a pessoa sem como voltar.
    const atual = contratosLista.find(c => c.id === form.contrato_id);
    if (atual && !idsDoCliente.has(atual.id)) {
      grupos.push({ label: 'Contrato atual desta parcela',
        options: [{ value: atual.id, label: rotulo(atual) }] });
    }
    if (doCliente.length) {
      grupos.push({ label: 'Contratos deste cliente',
        options: doCliente.slice().sort(ordena).map(c => ({ value: c.id, label: rotulo(c) })) });
    }
    grupos.push({
      label: doCliente.length ? `Outros contratos (${outros.length})` : `Contratos (${outros.length})`,
      options: outros.slice().sort(ordena)
        .filter(c => !atual || c.id !== atual.id)
        .map(c => ({ value: c.id, label: rotulo(c) })),
    });
    return grupos;
  }, [contratosLista, form.cliente_id, form.contrato_id]);

  // Contratante do contrato vinculado — usado pra sinalizar quando a parcela
  // fatura pra outro CNPJ.
  const contratoVinculado = useMemo(
    () => contratosLista.find(c => c.id === form.contrato_id) || null,
    [contratosLista, form.contrato_id]);
  const faturaParaOutro = !!(contratoVinculado?.cliente_id && form.cliente_id
    && parseInt(form.cliente_id) !== contratoVinculado.cliente_id);
  // Quem recebe a NF para a aba Dados pra NF: o "Faturar para" do FORM (a
  // pessoa pode ter acabado de trocar) ou, em branco, o contratante. O nome só
  // é conhecido quando não mudou — senão o seletor mentiria o cliente antigo.
  const clienteNfId: number | null = form.cliente_id ? parseInt(form.cliente_id)
    : (contratoVinculado?.cliente_id ?? (parcela as any).contratante_id ?? null);
  const clienteNfNome: string | null = form.cliente_id
    ? (String(parcela.cliente_id ?? '') === form.cliente_id ? (parcela.cliente_nome || null) : null)
    : (contratoVinculado?.cliente_nome || (parcela as any).contratante_nome || parcela.cliente_nome || null);

  // Categoria dirige os campos (pedido Lucas 2026-07-08): rendimento, aporte,
  // transferência etc. não pedem contrato — esconde a vinculação (a menos que
  // a parcela JÁ tenha contrato, pra pessoa poder limpar/corrigir).
  const TIPOS_SEM_CONTRATO = ['receita_financeira', 'aporte_capital',
    'outras_receitas', 'transferencia_entre_contas', 'mutuo_socio', 'emprestimo_captado'];
  const semContrato = TIPOS_SEM_CONTRATO.includes(form.tipo_receita || '') && !form.contrato_id;

  // Mesma ideia, agora para os campos de NOTA FISCAL (pedido do Lucas em
  // 2026-09-08, olhando a captação do Pronampe: "o ideal seria a tela alterar
  // para os campos pertinentes quando se altera a categoria"). Dinheiro que o
  // banco liberou não tem nota, prazo de pagamento, glosa nem rateio por setor.
  //
  // 🚨 Esta lista espelha NAO_FATURAVEL de comercial-engine/handlers/
  // impostosUtil.js — e NÃO a TIPOS_SEM_CONTRATO acima, que responde a outra
  // pergunta (lá entra 'outras_receitas', que PODE ter NF). Divergir daqui faz
  // a tela esconder a NF de uma parcela que o servidor vai tributar, ou o
  // contrário. O teste tests/receita-dre/categoria-campos.test.js trava as duas.
  const TIPOS_SEM_NF = ['receita_financeira', 'aporte_capital',
    'transferencia_entre_contas', 'mutuo_socio', 'emprestimo_captado'];
  // Patrimoniais = os quatro que a vw_receita_dre exclui. Só esses dispensam
  // rateio por setor. 'receita_financeira' fica DE FORA de propósito: ela É
  // receita na DRE, e receita sem rateio soma zero no relatório (index.js:1179).
  const TIPOS_PATRIMONIAIS = ['transferencia_entre_contas', 'aporte_capital',
    'mutuo_socio', 'emprestimo_captado'];
  const naoFaturavel = TIPOS_SEM_NF.includes(form.tipo_receita || '');
  const patrimonial = TIPOS_PATRIMONIAIS.includes(form.tipo_receita || '');

  // Uma linha explicando o que a tela acabou de fazer — sem isso a pessoa troca
  // a categoria e metade dos campos some sem explicação.
  const AVISO_CATEGORIA: Record<string, string> = {
    emprestimo_captado: 'Entrada de empréstimo do banco: é dívida, não receita. Não tem nota fiscal, retenção de imposto nem rateio por setor — as prestações e os juros ficam no módulo Empréstimos.',
    mutuo_socio: 'Devolução de mútuo do sócio: é a volta de um dinheiro emprestado, não receita. Sem nota fiscal e sem retenção.',
    aporte_capital: 'Aporte de capital: entra no patrimônio, não na receita. Sem nota fiscal e sem retenção.',
    transferencia_entre_contas: 'Transferência entre contas nossas: o dinheiro só mudou de banco. Não é receita e não tem nota fiscal.',
    receita_financeira: 'Rendimento de aplicação: quem paga é o banco, então não há nota fiscal nem retenção — mas continua sendo receita na DRE.',
  };

  // FIN-7ajustes (D): corpo da NF derivado = objeto do contrato (vem no detalhe)
  // + a parcela (ex.: "[25026]: BM11 - julho/26"). Usado como valor padrão do
  // campo editável descricao_nf.
  const corpoNfDerivado = [detalhe?.contrato_objeto, parcela.parcela_titulo || parcela.descricao]
    .map(s => (s || '').trim()).filter(Boolean).join(' — ');

  // "Não há NF" = nf_situacao 'dispensada' no backend (sem imposto devido).
  // Espelha NF_TEXTOS_DISPENSADA de impostosUtil.js — o campo é texto livre e
  // é o texto que classifica; o checkbox só escreve/limpa a forma canônica.
  const NF_TEXTO_SEM = 'Não há';
  const semNf = ['não há', 'nao ha', 'não ha', 'nao há', 'sem nf', 'sem',
    'n/a', 'na', 's/n', 'sn', '-', '—', 'não tem', 'nao tem',
    'sem número', 'sem numero'].includes((form.nota_fiscal_numero || '').trim().toLowerCase());

  // 🚨 REGRA DE OURO deste bloco: só some da tela campo VAZIO. Campo com dado
  // gravado continua aparecendo — sumir com ele seria esconder justamente o que
  // alguém precisa ver para corrigir. E o que some da tela sai também do
  // payload (ver salvarDetalhes): o update do servidor é PARCIAL, então chave
  // ausente = o banco preserva o que tinha, nunca apaga.
  const emBranco = (v: any) => v === null || v === undefined || String(v).trim() === '';
  // Emissão e prazo andam JUNTOS: são as duas metades do vencimento calculado
  // (vencimentoCalculado, logo abaixo). Esconder só um deixaria "Data de
  // vencimento" travada em cinza citando um campo invisível, sem jeito de
  // destravar pela tela. Some o par, e só quando os dois estão em branco — aí
  // o vencimento já era editável de qualquer forma.
  const escNfDatas = naoFaturavel && emBranco(form.nota_fiscal_emissao) && form.prazo_dias == null;
  // "Não há" é o texto canônico de "sem NF": para uma categoria que nunca tem
  // nota, ele não carrega informação nenhuma — o servidor já grava dispensada.
  const escNfNumero = naoFaturavel && (emBranco(form.nota_fiscal_numero) || semNf);
  const escDescontos = naoFaturavel && !(Number(form.descontos) > 0);
  const rateioAtual = (detalhe?.rateio_setores ?? parcela.rateio_setores) as Record<string, number> | null | undefined;
  const escRateio = patrimonial
    && !Object.values(rateioAtual || {}).some(v => typeof v === 'number' && v > 0);
  const escAbaNf = naoFaturavel && escNfNumero && emBranco(form.cnae) && emBranco(form.descricao_nf)
    && !((detalhe?.emails_nf_destinatarios ?? parcela.emails_nf_destinatarios ?? []).length);
  // A aba de NF sumindo com a pessoa parada nela deixaria o corpo do modal em
  // branco com o rodapé de Salvar vivo. Devolve pra Detalhes.
  useEffect(() => {
    if (escAbaNf && aba === 'nf') setAba('detalhes');
  }, [escAbaNf, aba]);

  // Sprint F8.3 — vencimento calculado quando há emissão + prazo definidos.
  // Quando o user altera qualquer um dos dois, recalcula e atualiza data_vencimento.
  // Se um dos dois faltar, o campo vencimento volta a ser editável manualmente.
  // (escNfDatas só é verdadeiro com emissão E prazo em branco — nesse caso este
  // memo já devolve null e o vencimento fica editável. Não há travamento
  // possível com o campo escondido.)
  const vencimentoCalculado = useMemo(() => {
    if (!form.nota_fiscal_emissao || form.prazo_dias == null || form.prazo_dias < 0) return null;
    // Soma dias em UTC pra evitar shift de timezone
    const d = new Date(form.nota_fiscal_emissao + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + Number(form.prazo_dias));
    return d.toISOString().slice(0, 10);
  }, [form.nota_fiscal_emissao, form.prazo_dias]);

  // ...mas só depois que a PESSOA mexer na emissão ou no prazo. O prazo agora
  // é hidratado do detalhe ao abrir (antes vinha sempre null, por bug), e sem
  // esta trava o modal recalcularia o vencimento sozinho ao abrir — passando
  // por cima de vencimento ajustado à mão, sem ninguém pedir.
  const mexeuNoPrazoOuEmissao = useRef(false);

  useEffect(() => {
    // Quando há cálculo válido e diverge do salvo, atualiza o form.
    if (!mexeuNoPrazoOuEmissao.current) return;
    if (vencimentoCalculado && vencimentoCalculado !== form.data_vencimento) {
      setForm(f => ({ ...f, data_vencimento: vencimentoCalculado }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vencimentoCalculado]);

  // Datas→status (Lucas 2026-08-05): vencimento indo pro futuro tira a
  // parcela de 'atrasada' sozinho — faturada se a NF é real, senão pendente.
  // Espelho da normalização do backend (motivo 'vencimento_futuro'); aqui é
  // pro select não mentir com o modal aberto. Re-selecionar Atrasada à mão
  // vale — o efeito só re-roda quando o VENCIMENTO muda.
  const [statusAutoAjustado, setStatusAutoAjustado] = useState(false);
  useEffect(() => {
    if (form.status !== 'atrasada' || !form.data_vencimento) return;
    if (form.data_vencimento < hojeIso()) return;
    const nfReal = (form.nota_fiscal_numero || '').trim() !== '' && !semNf;
    const novo = nfReal ? 'faturada' : 'pendente';
    setForm(f => ({ ...f, status: novo }));
    // A base acompanha: quem mudou foi o sistema, não a pessoa — senão o
    // rodapé acusaria "alterações não salvas" só por abrir a parcela.
    formBase.current = { ...formBase.current, status: novo };
    setStatusAutoAjustado(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.data_vencimento]);

  // Amarração competência × emissão (Lucas 2026-08-05): política contábil é
  // "competência = faturamento" — divergir de MÊS é exceção e ganha tag âmbar.
  const mesComp = (form.data_competencia || '').slice(0, 7);
  const mesEmissao = (form.nota_fiscal_emissao || '').slice(0, 7);
  const competenciaDiverge = !!mesComp && !!mesEmissao && mesComp !== mesEmissao;
  const mesBr = (m: string) => `${m.slice(5, 7)}/${m.slice(0, 4)}`;

  // Extraído porque o vencimento muda de vizinho conforme a categoria: com NF
  // ele fica ao lado do Prazo; sem NF (escNfDatas) sobe pro lado do Valor
  // previsto, no lugar que era da emissão — assim a linha não fica pela metade.
  const campoVencimento = (
    <CampoMP label={vencimentoCalculado ? 'Vencimento (calculado)' : 'Data de vencimento'}>
      <div style={{ position: 'relative' }}>
        <input type="date" value={form.data_vencimento}
          onChange={e => setForm({ ...form, data_vencimento: e.target.value })}
          disabled={!!vencimentoCalculado}
          title={vencimentoCalculado
            ? `Calculado: emissão (${form.nota_fiscal_emissao}) + ${form.prazo_dias} dias`
            : 'Preencha emissão e prazo pra calcular automaticamente'}
          style={{
            ...inputMP,
            background: vencimentoCalculado ? '#F0F1F5' : '#fff',
            color: vencimentoCalculado ? '#444' : undefined,
            cursor: vencimentoCalculado ? 'not-allowed' : 'text',
          }} />
        {vencimentoCalculado && (
          <span style={{
            position: 'absolute', right: 28, top: '50%', transform: 'translateY(-50%)',
            fontSize: 9, padding: '1px 6px', borderRadius: 8,
            background: '#0930D0', color: '#fff', fontWeight: 600, pointerEvents: 'none',
          }}>CALCULADO</span>
        )}
      </div>
    </CampoMP>
  );

  // Entrada de pagamento começada e não confirmada (o ✓ do card "Entradas
  // desta parcela"). Com o rodapé em toda aba, o caminho natural virou clicar
  // em "Salvar alterações" com a entrada pela metade — e ela ia para o lixo
  // sem uma palavra (revisão adversarial de 30/09).
  const entradaEmDigitacao = (novaEntrada.aberto
      && String(novaEntrada.valor ?? '').trim() !== '')
    || editandoEntrada != null;
  // O que está mexido e ainda não foi gravado. Uma lista só, usada pelo aviso
  // do rodapé, pela pergunta ao fechar e por quem sai do modal por outra
  // porta — 30/09/2026, quando o rateio ainda tinha Salvar próprio e o resto
  // do formulário saía calado.
  const pendencias = [
    // Duas pistas somadas: o formulário comparado com a base (o estado de
    // quando abriu/hidratou) e os campos que a pessoa tocou — a segunda cobre
    // quem digitou ANTES de o detalhe chegar.
    ...((camposAlterados(form, formBase.current).length > 0
      || camposMexidos.current.size > 0 || mexeuTituloDesc.current)
      ? ['no formulário'] : []),
    ...(rateioPct != null ? ['no rateio por setor'] : []),
    ...((destAlterado || destDigitado.trim()) ? ['nos destinatários da NF'] : []),
    ...(entradaEmDigitacao ? ['numa entrada de pagamento'] : []),
  ];

  async function salvarDetalhes() {
    // E-mail digitado e não incluído (sem Enter): o Salvar inclui o que é
    // e-mail válido — antes o modal fechava e a NF saía sem esse destinatário,
    // calada (revisão adversarial de 21/09). O que NÃO parece e-mail barra o
    // Salvar, com a aba aberta no campo para corrigir.
    //
    // Entrada de pagamento pela metade: o Salvar da parcela não a registra
    // (ela tem endpoint e validação próprios). Em vez de engolir, manda
    // confirmar ou cancelar — perder o que foi digitado seria pior.
    if (entradaEmDigitacao) {
      setAba('pagamento');
      alert('Há uma entrada de pagamento começada e não confirmada na aba '
        + 'Pagamento. Confirme no ✓ (ou cancele) e salve de novo — o Salvar '
        + 'da parcela não registra a entrada.');
      return;
    }
    let listaDest = destinatarios;
    let mexeuDest = mexeuDestinatarios.current;
    if (destDigitado.trim() && !escAbaNf) {
      const r = incluirAvulsos(destinatarios, contatosNfRef.current, destDigitado);
      if (r.restante.trim()) {
        setAba('nf');
        alert(`Na aba Dados pra NF ficou um texto no campo de e-mail que não parece e-mail: `
          + `"${r.restante.trim()}". Corrija e tecle Enter, ou apague, e salve de novo.`);
        return;
      }
      if (r.incluidos) { listaDest = r.lista; mexeuDest = true; }
    }
    setSalvando(true);
    try {
      const token = await getToken();
      // Sprint F15 — contrato_id pode ser '' (avulso) → manda null pro Postgres
      // não engasgar tentando converter string vazia em UUID.
      const payload: any = { ...form };
      // 🚨 Só viaja o que a pessoa ALTEROU (30/09/2026). Mandar o formulário
      // inteiro era inofensivo enquanto o rodapé só existia nos Detalhes; com
      // ele em TODA aba, o caminho natural virou: registrar uma entrada na aba
      // Pagamento (o servidor passa a parcela para "recebida") e clicar em
      // Salvar — que reenviava o status ANTIGO do formulário e DESFAZIA o
      // recebimento. O servidor faz update parcial (`b[k] !== undefined` em
      // contasReceber.js), então chave ausente = o banco preserva o que tem,
      // e cada regra de lá já lê `b.x !== undefined ? b.x : prev.x`.
      const alterados = new Set(camposAlterados(form, formBase.current));
      for (const k of Object.keys(payload)) {
        if (!alterados.has(k) && !camposMexidos.current.has(k)) delete payload[k];
      }
      // 🚨 Retenção é derivada de tipo_receita + nf_situacao + valor, e o
      // servidor só reclassifica `nf_situacao` quando RECEBE o número da NF.
      // Mudar só a categoria (de uma sem NF para "contrato", por exemplo)
      // deixaria líquido e impostos na base antiga — dinheiro errado na
      // conciliação e na DRE. Então a NF viaja junto com a categoria.
      if ('tipo_receita' in payload) payload.nota_fiscal_numero = form.nota_fiscal_numero;
      // Campo que a CATEGORIA escondeu sai do payload. O update do servidor é
      // parcial (`if (b[k] !== undefined)` em contasReceber.js), então chave
      // ausente = o banco preserva o que tinha — nunca apaga. Tem que vir ANTES
      // das coerções abaixo, senão elas recriam a chave com '' → null → 0.
      // 🚨 Só chega aqui campo que estava em branco (ver a REGRA DE OURO lá em
      // cima): nada gravado é descartado por esconder.
      if (escNfDatas) { delete payload.nota_fiscal_emissao; delete payload.prazo_dias; }
      if (escNfNumero) delete payload.nota_fiscal_numero;
      if (escDescontos) { delete payload.descontos; delete payload.descontos_motivo; }
      if (escAbaNf) { delete payload.cnae; delete payload.descricao_nf; }
      // 🚨 `'campo' in payload` em TODA coerção: sem isso ela RECRIA a chave
      // que o filtro acabou de tirar e grava null por cima do banco.
      if ('contrato_id' in payload
        && (payload.contrato_id === '' || payload.contrato_id == null)) payload.contrato_id = null;
      // F-CR-cliente: cliente_id pode ser '' (sem cliente) — manda null
      if ('cliente_id' in payload) {
        if (payload.cliente_id === '' || payload.cliente_id == null) payload.cliente_id = null;
        else payload.cliente_id = parseInt(payload.cliente_id);
      }
      // F-CR-dates: campos DATE com "" quebram o Postgres ("invalid input syntax
      // for type date"). Converte "" → null nos campos de data DO FORMULÁRIO.
      //
      // 🚨 `data_recebimento` saiu desta lista (27/08/2026). Ela NÃO é campo do
      // formulário — quem manda na data de recebimento é a aba Pagamento. Como
      // a linha abaixo cria a chave quando ela não existe, todo "Salvar
      // alterações" mandava `data_recebimento: null` e APAGAVA a data em que o
      // dinheiro entrou: 54 vezes em 35 parcelas, a última em 26/08.
      // O `k in payload` fecha a porta de vez: só se coage o que o formulário
      // realmente tem, então acrescentar um nome aqui nunca mais inventa campo.
      ['nota_fiscal_emissao', 'data_vencimento', 'data_competencia'].forEach(k => {
        if (!(k in payload)) return;
        if (payload[k] === '' || payload[k] == null) payload[k] = null;
      });
      // F-CR-numbers: campos numericos opcionais '' → null
      if ('prazo_dias' in payload
        && (payload.prazo_dias === '' || payload.prazo_dias == null)) payload.prazo_dias = null;
      // FIN-7ajustes: descontos sempre número; descricao_nf '' → null (mantém a
      // derivação automática {objeto} — {parcela} no display quando não editada).
      if ('descontos' in payload) payload.descontos = Number(payload.descontos) || 0;
      if ('descricao_nf' in payload
        && (payload.descricao_nf === '' || payload.descricao_nf == null)) payload.descricao_nf = null;
      if ('cnae' in payload
        && (payload.cnae === '' || payload.cnae == null)) payload.cnae = null;
      // FZ 5.2: '' quebraria o cast pra uuid no Postgres
      if ('conta_financeira_id' in payload
        && (payload.conta_financeira_id === '' || payload.conta_financeira_id == null)) {
        payload.conta_financeira_id = null;
      }
      // Título × Descrição: a régua única (parcelaForm.ts) decide o que vai em
      // cada coluna — em modo único, o MESMO texto nas duas (o e-mail "NF a
      // emitir" lê descricao crua); em divergente, cada campo na sua. As
      // chaves saem do payload ANTES: quando a régua devolve {} (título
      // esvaziado numa parcela que tinha texto), nada é gravado e o banco
      // preserva o que tinha.
      // O rateio é conferido AQUI, antes do aviso do título: recusar depois
      // dele fazia a tela dizer "os demais campos foram salvos" sem nada ter
      // sido gravado.
      const rat = montarPayloadRateio(
        rateioPct, semearRateioPct(detalhe?.rateio_setores ?? parcela.rateio_setores,
          Object.keys(SETORES)));
      if ('erro' in rat) { setAba('detalhes'); alert(rat.erro); return; }
      if (rat.campos) Object.assign(payload, rat.campos);
      delete payload.parcela_titulo;
      delete payload.descricao;
      const tituloDesc = montarPayloadTituloDescricao(
        modoTituloDesc, form.parcela_titulo, form.descricao, haviaTextoTitulo.current);
      Object.assign(payload, tituloDesc);
      if (!Object.keys(tituloDesc).length) {
        alert('O título ficou vazio e NÃO foi salvo — mantive o texto anterior ' +
          '(ele também é a descrição que sai nos e-mails automáticos). ' +
          'Os demais campos foram salvos normalmente.');
      }
      // Porta do 'falhou' (revisão adversarial de 01/09): sem o detalhe, os
      // campos que SÓ ele hidrata e que a pessoa NÃO mexeu saem do payload —
      // o update parcial do backend preserva o valor do banco. Sem isso, o
      // form vazio apagava observações/corpo da NF/prazo (mecanismo do
      // incidente de 21/08, agora pela porta lateral).
      if (detalheStatus !== 'ok') {
        for (const k of ['observacoes', 'descricao_nf', 'prazo_dias']) {
          if (!camposMexidos.current.has(k)) delete payload[k];
        }
        if (!mexeuTituloDesc.current) {
          delete payload.parcela_titulo;
          delete payload.descricao;
        }
      }
      // Destinatários da NF: a chave só entra com o detalhe 'ok' E a pessoa
      // tendo mexido — senão uma lista vazia gravaria por cima da real
      // (mecanismo do incidente de 21/08; a lista é jsonb, sem rastro).
      Object.assign(payload, montarPayloadDestinatarios({
        lista: listaDest,
        detalheOk: detalheStatus === 'ok',
        mexeu: mexeuDest,
        legadoOferecido: legadoOferecido.current,
        abaNfEscondida: escAbaNf,
      }));
      // Nada mudou: nem chama o servidor. Com o payload parcial isso passou
      // a acontecer (antes o form inteiro sempre ia), e o servidor responderia
      // 400 "Nenhum campo para atualizar" DEPOIS de gravar uma versão inútil
      // no histórico da parcela.
      if (!Object.keys(payload).length) { onClose(); return; }
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`, {
        action: 'fin_atualizar_conta_receber',
        id: parcela.id,
        ...payload,
      }, { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      // Sprint F8.6: aviso quando o backend transitou status automaticamente
      // (ex.: NF foi preenchida e a parcela virou faturada sozinha).
      if (d?._auto_status?.motivo === 'nf_preenchida') {
        alert(
          `NF preenchida — status atualizado automaticamente de ` +
          `"${STATUS_PARC[d._auto_status.de]?.label || d._auto_status.de}" ` +
          `para "Faturada".`
        );
      } else if (d?._auto_status?.motivo === 'vencimento_futuro') {
        // Backend normalizou 'atrasada' com vencimento futuro (o modal já
        // flipa o select sozinho; este alerta cobre quem forçou Atrasada).
        alert(
          `Vencimento no futuro — status atualizado automaticamente de ` +
          `"${STATUS_PARC[d._auto_status.de]?.label || d._auto_status.de}" ` +
          `para "${STATUS_PARC[d._auto_status.para]?.label || d._auto_status.para}".`
        );
      }
      onMudou();
      onClose();
    } catch (e: any) {
      alert('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  // Pagamento (tab) — o registro de recebimento vive no card "Entradas desta
  // parcela" (modelo de entradas-filhas, 2026-07-08). O form antigo
  // Adicionar/Corrigir recebimento (fin_marcar_recebida direto na linha)
  // foi aposentado — era redundante e deixava recebido "fantasma" quando o
  // status era revertido na mão (caso Arroio dos Ratos).
  const [calc, setCalc] = useState<{
    liquido: number; federal_pct: number; iss_pct: number; retem_iss: boolean;
    valor_previsto: number; valor_recebido: number; falta: number;
  } | null>(null);

  // Carrega cálculo do líquido quando abre a aba pagamento
  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'fin_calcular_liquido_parcela', id: parcela.id },
          { headers: { Authorization: token } });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        setCalc(d);
      } catch { /* falha de calc não bloqueia fluxo */ }
    })();
  }, [parcela.id, parcela.status]);

  // Aprovar/recusar (gerente do contrato)
  async function aprovarGerente() {
    if (!confirmarDescartar('Aprovar')) return;
    setSalvando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`, {
        action: 'fin_aprovar_parcela_gerente', id: parcela.id,
      }, { headers: { Authorization: token } });
      onMudou(); onClose();
    } catch (e: any) { alert('Erro: ' + (e.response?.data?.error || e.message)); }
    finally { setSalvando(false); }
  }
  async function recusarGerente() {
    if (!confirmarDescartar('Recusar')) return;
    const motivo = window.prompt('Motivo da recusa:');
    if (!motivo?.trim()) return;
    setSalvando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`, {
        action: 'fin_recusar_parcela_gerente', id: parcela.id, motivo,
      }, { headers: { Authorization: token } });
      onMudou(); onClose();
    } catch (e: any) { alert('Erro: ' + (e.response?.data?.error || e.message)); }
    finally { setSalvando(false); }
  }

  // Sprint F4 — Excluir parcela com dupla checagem por texto.
  // Backend exige `confirmacao: 'EXCLUIR'` no payload.
  async function excluirParcela() {
    if (parcela.status === 'recebida' && parcela.valor_recebido) {
      alert(
        'Esta parcela já foi recebida e não pode ser excluída (preserva histórico fiscal). ' +
        'Use cancelamento se necessário.'
      );
      return;
    }
    const confirmacao = window.prompt(
      `⚠ EXCLUIR PARCELA — operação irreversível.\n\n` +
      `Parcela: ${parcela.parcela_titulo || parcela.descricao || parcela.id}\n` +
      `Valor: R$ ${(parcela.valor_previsto || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n` +
      `Contrato: ${parcela.contrato_numero || '—'}\n\n` +
      `Pra confirmar, digite EXCLUIR abaixo:`,
      ''
    );
    if (confirmacao !== 'EXCLUIR') {
      if (confirmacao !== null) alert('Texto não confere. Exclusão cancelada.');
      return;
    }
    setSalvando(true);
    try {
      const token = await getToken();
      // Usa fin_excluir_parcela (versão com checagem de permissão via contrato_id)
      // se a parcela tem contrato; senão usa fin_excluir_conta_receber.
      const action = parcela.contrato_id ? 'fin_excluir_parcela' : 'fin_excluir_conta_receber';
      await axios.post(`${API_URL}/comercial/propostas-geradas`, {
        action, id: parcela.id, confirmacao: 'EXCLUIR',
      }, { headers: { Authorization: token } });
      onMudou(); onClose();
    } catch (e: any) {
      alert('Erro ao excluir: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  // Status badge
  const sp = STATUS_PARC[parcela.status] || STATUS_PARC.pendente;

  // FIN-UI (2026-07-14): portalizado pro body — dentro das Tabs do hub, o
  // transform da troca de aba fazia o position:fixed ancorar no painel (alto)
  // e o modal "caía" pra baixo da dobra. Portal = sempre centrado na janela.
  return createPortal(
    // Regra da casa (CLAUDE.md): modal usa a tela — min(94vw, teto), corpo é o
    // único que rola. alignItems flex-start + padding 16 = o "top: 16" do antd.
    <div onClick={fecharModal} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 8, width: 'min(94vw, 1360px)',
        maxHeight: 'calc(100vh - 32px)', overflow: 'hidden', display: 'flex', flexDirection: 'column',
        boxShadow: '0 12px 48px rgba(0,0,0,0.2)' }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '0.5px solid #e0e0e0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: '#888', fontFamily: 'monospace' }}>
              {parcela.contrato_numero}
              {parcela.numero_parcela && ` · Parcela ${parcela.numero_parcela}/${parcela.total_parcelas || '?'}`}
            </div>
            <h3 style={{ margin: '4px 0 2px', fontSize: 15, color: '#020D7F' }}>
              {parcela.parcela_titulo || parcela.descricao || 'Parcela sem título'}
            </h3>
            <div style={{ fontSize: 11, color: '#666' }}>
              {parcela.cliente_nome || '—'}
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 3,
                background: sp.bg, color: sp.cor, fontWeight: 600 }}>
                {sp.label}
              </span>
              {(parcela as any).aprovada_gerente_em && (
                <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 3,
                  background: '#E8F5EE', color: '#0a7f4a', fontWeight: 600 }}>
                  ✓ Aprovada gerente
                </span>
              )}
              {(parcela as any).recusada_em && (
                <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 3,
                  background: '#FDECEC', color: '#B91626', fontWeight: 600 }}
                  title={(parcela as any).recusada_motivo}>
                  ✕ Recusada
                </span>
              )}
              {parcela.liberada && (
                <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 3,
                  background: '#EFF3FF', color: '#0930D0', fontWeight: 600 }}>
                  🚀 Liberada cobrança
                </span>
              )}
            </div>
          </div>
          <button onClick={fecharModal} style={{ background: 'transparent', border: 'none',
            fontSize: 22, color: '#888', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        {/* Tabs */}
        <Tabs activeKey={aba} onChange={(k) => setAba(k as Aba)} size="small"
          style={{ padding: '0 12px' }}
          items={[
            { key: 'detalhes', label: 'Detalhes' },
            // A aba "📧 E-mails NF" foi absorvida pela Dados pra NF (2026-09-01):
            // destinatários, CNAE, corpo e tomador num lugar só.
            ...(escAbaNf ? [] : [{ key: 'nf', label: '🧾 Dados pra NF' }]),
            { key: 'pagamento', label: 'Pagamento' },
            { key: 'anexos', label: 'Anexos' },
            { key: 'historico', label: 'Histórico' },
          ]}
        />

        {/* Conteúdo */}
        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
          {aba === 'detalhes' && (<>
            {/* 2 colunas lógicas (auto-fit: colapsa pra 1 coluna em tela
                estreita, sem media query): A = identificação & vínculos;
                B = valores, datas e status. Rateio e aprovação full-width. */}
            <div style={{ display: 'grid', gap: '12px 24px', alignItems: 'start',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* F-CR-tipo no TOPO (pedido Lucas 2026-07-08): a categoria dirige
                  os campos do modal — rendimento não pede contrato; contrato
                  pede vinculação. */}
              <CampoMP label="Categoria (tipo de receita)">
                <select value={form.tipo_receita || ''}
                  onChange={e => setForm({ ...form, tipo_receita: e.target.value || null })}
                  style={inputMP}>
                  <option value="">— sem classificação —</option>
                  <option value="contrato">📄 Contrato (operacional)</option>
                  <option value="reembolso_contrato">↩️ Reembolso/estorno de contrato (fechamento sim, receita não)</option>
                  <option value="receita_financeira">📈 Receita financeira (rendimento)</option>
                  <option value="aporte_capital">💼 Aporte de capital</option>
                  <option value="outras_receitas">➕ Outras receitas (estornos, ajustes)</option>
                  <option value="transferencia_entre_contas">🔄 Transferência entre contas (não é receita)</option>
                  <option value="mutuo_socio">🤝 Mútuo com sócio (devolução de empréstimo — não é receita)</option>
                  <option value="emprestimo_captado">🏦 Empréstimo captado do banco (não é receita — é dívida)</option>
                  <option value="nao_classificada">⚠️ Não classificada (revisar)</option>
                </select>
              </CampoMP>
              {!!AVISO_CATEGORIA[form.tipo_receita || ''] && (
                <div style={{ fontSize: 11, color: '#31356E', background: '#F3F5FF',
                  border: '0.5px solid #d8dcec', borderRadius: 5, padding: '7px 10px',
                  lineHeight: 1.55 }}>
                  {AVISO_CATEGORIA[form.tipo_receita || '']}
                </div>
              )}
              {/* Multi-CNPJ (2026-08-04): "Faturar para" é o tomador da NF, que
                  pode ser diferente de quem assinou o contrato (CERILUZ contrata
                  pras três usinas e pede nota no CNPJ de cada uma). Trocar aqui
                  NÃO mexe mais no contrato — antes zerava e a parcela virava
                  avulsa em silêncio. */}
              <div>
                <label style={{ fontSize: 11, color: '#666', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                  {semContrato ? 'Pagador' : 'Faturar para'} {!parcela.cliente_id && parcela.origem === 'conta_azul_api' && (
                    <span style={{ color: '#B07500', fontWeight: 400 }}>⚠ não vinculado (parcela CA órfã)</span>
                  )}
                </label>
                <SelectCliente
                  value={form.cliente_id ? parseInt(form.cliente_id) : null}
                  onChange={v => setForm({ ...form, cliente_id: v == null ? '' : String(v) })}
                  contratanteId={contratoVinculado?.cliente_id ?? null}
                  placeholder={contratoVinculado ? '— usar o contratante do contrato' : '— sem cliente'}
                />
                {faturaParaOutro && (
                  <div style={{ fontSize: 10, color: '#7C5800', marginTop: 4, lineHeight: 1.5 }}>
                    A nota desta parcela sai para <strong>{(parcela as any).cliente_nome || 'o CNPJ acima'}</strong>,
                    mas ela continua no contrato <strong>{contratoVinculado?.numero}</strong>
                    {contratoVinculado?.cliente_nome ? <> (contratante: {contratoVinculado.cliente_nome})</> : null}.
                  </div>
                )}
                {!parcela.cliente_id && parcela.origem === 'conta_azul_api' && (
                  <div style={{ fontSize: 10, color: '#7C5800', marginTop: 4 }}>
                    Nome do CA: <strong>{(parcela as any).cliente_nome || '?'}</strong> — escolha o cliente cadastrado correspondente acima
                  </div>
                )}
              </div>

              {/* Sprint F15 — re-vincular parcela a outro contrato (pai/aditivo).
                  Útil quando você criou a parcela no pai mas deveria estar num aditivo.
                  Escondido quando a categoria não pede contrato (rendimento etc). */}
              {!semContrato && (
              <div>
                <label style={{ fontSize: 11, color: '#666', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                  Contrato vinculado <span style={{ color: '#888', fontWeight: 400 }}>(busque por número, título ou cliente)</span>
                </label>
                <Select
                  showSearch allowClear
                  value={form.contrato_id || undefined}
                  onChange={v => setForm({ ...form, contrato_id: v || '' })}
                  placeholder="— sem contrato (lançamento avulso)"
                  style={{ width: '100%' }}
                  filterOption={(input, opt) =>
                    String((opt as any)?.label || '').toLowerCase().includes((input || '').toLowerCase().trim())}
                  options={gruposContrato}
                />
                {parcela.contrato_id && form.contrato_id !== parcela.contrato_id && (
                  <div style={{ fontSize: 10, color: '#B07500', marginTop: 4 }}>
                    ⚠ Você está movendo essa parcela pra outro contrato. Confirme antes de salvar.
                  </div>
                )}
              </div>
              )}

              {/* F-CR-contrato-externo: rotulo livre quando contrato nao esta
                  no operacional (pre-sistema, antigos, avulsos) */}
              {!semContrato && !form.contrato_id && (
                <div>
                  <label style={{ fontSize: 11, color: '#666', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                    Contrato externo <span style={{ color: '#999', fontWeight: 400 }}>(opcional — texto livre, sem vínculo ao módulo Operacional)</span>
                  </label>
                  <input value={form.contrato_externo}
                    onChange={e => setForm({ ...form, contrato_externo: e.target.value })}
                    placeholder="ex: HY23045, Contrato Cliente X 2022, etc"
                    style={inputMP} />
                  <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                    Use pra rotular contratos não cadastrados (pré-sistema 2021-2023, contratos avulsos). Não cria contrato no Operacional.
                  </div>
                </div>
              )}

              {/* Rastreio parcela -> venda (Lucas 2026-07-08): quando o contrato
                  da parcela veio do comercial, linka o negócio de origem (lá
                  estão o documento de venda, PDFs e anexos). */}
              {(parcela as any).negocio_id && (
                <button type="button"
                  onClick={() => {
                    if (!confirmarDescartar('Sair daqui para a venda')) return;
                    // Mesmo fluxo do "Ver proposta" das Licitações (L1 fix)
                    sessionStorage.setItem('abrir_proposta_id', String((parcela as any).negocio_id));
                    window.dispatchEvent(new CustomEvent('abrir-proposta-no-pipeline', {
                      detail: { propostaId: (parcela as any).negocio_id },
                    }));
                    navigate('/comercial/pipeline');
                    onClose();
                  }}
                  style={{ alignSelf: 'flex-start', padding: '5px 12px', fontSize: 11,
                    fontWeight: 600, background: '#F4F7FF', color: '#0930D0',
                    border: '0.5px solid #C4D2FF', borderRadius: 4, cursor: 'pointer' }}>
                  📄 Ver venda de origem (negócio #{(parcela as any).negocio_id})
                </button>
              )}

              {/* Título é o rótulo da medição na lista ("[26006]: Medição 05").
                  Campo ÚNICO desde 2026-09-01: título e descrição eram gêmeos em
                  quase toda parcela (regra em parcelaForm.ts; o salvar grava as
                  DUAS colunas). A "Descrição detalhada" só aparece quando as
                  colunas divergem de verdade (aditivo: motivo; parcela CA). */}
              <CampoMP label="Título da parcela (rótulo na lista)">
                <input value={form.parcela_titulo}
                  onChange={e => { mexeuTituloDesc.current = true;
                    if (detalheStatus !== 'ok') setEdicaoAntecipada(true);
                    setForm({ ...form, parcela_titulo: e.target.value }); }}
                  placeholder="ex: [26006]: Medição 05" style={inputMP} />
                {edicaoAntecipada && (
                  <div style={{ fontSize: 10, color: '#B07500', marginTop: 3 }}>
                    ⚠ Editado antes de os dados completos carregarem — se o texto
                    original era muito longo, a base pode estar cortada em 200
                    caracteres. Confira antes de salvar.
                  </div>
                )}
              </CampoMP>
              {modoTituloDesc === 'divergente' && (
                <CampoMP label="Descrição detalhada (difere do título)">
                  <textarea rows={2} value={form.descricao}
                    onChange={e => { mexeuTituloDesc.current = true;
                      if (detalheStatus !== 'ok') setEdicaoAntecipada(true);
                      setForm({ ...form, descricao: e.target.value }); }}
                    style={{ ...inputMP, resize: 'vertical', fontFamily: 'inherit' }} />
                  <div style={{ fontSize: 10, color: '#888', marginTop: 3 }}>
                    Esta parcela guarda um texto diferente do título (ex.: motivo do
                    aditivo, texto do Conta Azul) — os e-mails automáticos usam este.
                    Esvaziar faz o título valer pros dois.
                  </div>
                </CampoMP>
              )}
              {/* F-CR-merge: botão de mesclar com parcela existente do sistema.
                  Aparece pra parcelas CA órfãs (id_externo_ca preenchido mas
                  ainda não vinculadas a contrato) — irrelevante pra categorias
                  sem contrato (rendimento etc). */}
              {!semContrato && parcela.id_externo_ca && !parcela.contrato_id && (
                <div style={{
                  padding: 10, background: '#FAFBFF', border: '0.5px dashed #C4D2FF',
                  borderRadius: 5,
                }}>
                  <div style={{ fontSize: 11, color: '#444', marginBottom: 6 }}>
                    🔗 <strong>Parcela vinda do Conta Azul sem vínculo a contrato.</strong>
                    {' '}Se essa parcela já existe no sistema (parcela do plano),
                    mescle pra não ficar duplicada.
                  </div>
                  <button type="button" onClick={() => setMostrarMerge(true)}
                    style={{ padding: '6px 12px', fontSize: 11, fontWeight: 600,
                      background: '#0930D0', color: '#fff', border: 'none',
                      borderRadius: 4, cursor: 'pointer' }}>
                    🔗 Mesclar com parcela existente…
                  </button>
                </div>
              )}
              {/* F-CR-merge-undo: botão desfazer vínculo CA (pra merges errados).
                  Aparece pra parcelas que TEM contrato_id + id_externo_ca
                  (foi mesclada antes). */}
              {parcela.id_externo_ca && parcela.contrato_id && (
                <div style={{
                  padding: 10, background: '#FFF8E8', border: '0.5px dashed #F0D88E',
                  borderRadius: 5,
                }}>
                  <div style={{ fontSize: 11, color: '#7A5500', marginBottom: 6 }}>
                    🔗 <strong>Esta parcela está vinculada ao Conta Azul.</strong>
                    {' '}Se foi um merge errado (parcela errada), desfaça o vínculo
                    e re-mescle com a parcela correta.
                  </div>
                  <button type="button" onClick={async () => {
                    if (!window.confirm(
                      'Desfazer o vínculo CA?\n\n' +
                      'A parcela volta a ser apenas do plano/sistema (sem dados do CA).\n' +
                      'A info da parcela CA original NÃO será recriada — você precisará\n' +
                      'mesclar com a parcela correta via "Mesclar com parcela existente"\n' +
                      'OU rodar novo sync CA pra trazer a CA de volta.\n\n' +
                      'Operação reversível pelo merge manual.'
                    )) return;
                    try {
                      const token = await getToken();
                      await axios.post(`${API_URL}/comercial/propostas-geradas`,
                        { action: 'fin_desfazer_merge_cr', id: parcela.id },
                        { headers: { Authorization: token } });
                      alert('Vínculo CA removido. A parcela voltou pra estado pré-merge.');
                      onMudou(); onClose();
                    } catch (e: any) {
                      alert('Erro: ' + (e.response?.data?.error || e.message));
                    }
                  }}
                    style={{ padding: '6px 12px', fontSize: 11, fontWeight: 600,
                      background: '#fff', color: '#B07500', border: '1px solid #B07500',
                      borderRadius: 4, cursor: 'pointer' }}>
                    🔓 Desfazer vínculo CA
                  </button>
                </div>
              )}
              <CampoMP label="Observações">
                <textarea value={form.observacoes}
                  onChange={e => { camposMexidos.current.add('observacoes');
                    setForm({ ...form, observacoes: e.target.value }); }}
                  rows={3} style={{ ...inputMP, resize: 'vertical', fontFamily: 'inherit' }} />
              </CampoMP>
            </div>
            {/* ——— Coluna B: valores, datas e status ——— */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <CampoMP label="Valor previsto (R$)">
                  <input type="number" step={0.01} value={form.valor_previsto}
                    onChange={e => setForm({ ...form, valor_previsto: Number(e.target.value) || 0 })}
                    style={inputMP} />
                </CampoMP>
                {escNfDatas ? campoVencimento : (
                <CampoMP label="Data de emissão NF">
                  <input type="date" value={form.nota_fiscal_emissao}
                    onChange={e => setForm(f => {
                      mexeuNoPrazoOuEmissao.current = true;
                      // Competência segue a emissão enquanto não foi divergida
                      // de propósito (vazia ou colada na emissão anterior).
                      const acompanha = !f.data_competencia
                        || f.data_competencia === f.nota_fiscal_emissao;
                      return { ...f, nota_fiscal_emissao: e.target.value,
                        data_competencia: acompanha ? e.target.value : f.data_competencia };
                    })}
                    style={inputMP} />
                </CampoMP>
                )}
              </div>
              {/* Sprint F8.3 — Prazo + Vencimento (calculado quando há emissão+prazo) */}
              {!escNfDatas && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <CampoMP label="Prazo (dias)">
                  <input type="number" min={0} max={365}
                    value={form.prazo_dias ?? ''}
                    onChange={e => { mexeuNoPrazoOuEmissao.current = true;
                      camposMexidos.current.add('prazo_dias');
                      setForm({ ...form, prazo_dias: e.target.value === '' ? null : parseInt(e.target.value) }); }}
                    placeholder="ex: 30" style={inputMP} />
                </CampoMP>
                {campoVencimento}
              </div>
              )}
              {/* FIN-7ajustes (2026-07-22): competência EDITÁVEL (antes só
                  exibia a espelhada do CA) + desconto livre por parcela (glosa,
                  saldo do cliente) que reduz o líquido a receber. */}
              <div style={{ display: 'grid',
                gridTemplateColumns: escDescontos ? '1fr' : '1fr 1fr', gap: 10 }}>
                <CampoMP label="Data de Competência (mês contábil)">
                  <input type="date" value={form.data_competencia}
                    onChange={e => setForm({ ...form, data_competencia: e.target.value })}
                    style={inputMP} />
                  {!!form.data_competencia && !!form.nota_fiscal_emissao && (
                    competenciaDiverge ? (
                      <div style={{ fontSize: 10, color: '#B07500', marginTop: 4,
                        display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span>⚠ difere da emissão — {naoFaturavel ? 'o lançamento entra em' : 'receita cai em'} <strong>{mesBr(mesComp)}</strong>, NF é de {mesBr(mesEmissao)}</span>
                        <button type="button"
                          onClick={() => setForm(f => ({ ...f, data_competencia: f.nota_fiscal_emissao }))}
                          style={{ fontSize: 10, padding: '1px 8px', background: '#FFF8E8',
                            color: '#7A5500', border: '0.5px solid #F0D88E', borderRadius: 4,
                            cursor: 'pointer' }}>
                          usar data da emissão
                        </button>
                      </div>
                    ) : (
                      <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                        = mês da emissão ({naoFaturavel ? 'o lançamento entra no mês da NF' : 'receita cai no mês da NF'})
                      </div>
                    )
                  )}
                </CampoMP>
                {!escDescontos && (
                <CampoMP label="Descontos (R$) — glosa/saldo">
                  <input type="number" step={0.01} min={0} value={form.descontos}
                    onChange={e => setForm({ ...form, descontos: Number(e.target.value) || 0 })}
                    style={inputMP} />
                </CampoMP>
                )}
              </div>
              {/* FZ 5.2 — conta bancária do recebimento (fluxo por banco). A
                  conciliação OFX preenche sozinha; aqui dá pra ajustar ou
                  definir a conta esperada de uma parcela prevista. */}
              <CampoMP label="Conta bancária (fluxo por banco)">
                <select value={form.conta_financeira_id}
                  onChange={e => setForm({ ...form, conta_financeira_id: e.target.value })}
                  style={inputMP}>
                  <option value="">— sem conta definida —</option>
                  {contasFin
                    .filter((c: any) => c.ativo !== false || c.id === form.conta_financeira_id)
                    .map((c: any) => (
                      <option key={c.id} value={c.id}>
                        {(c.apelido || c.nome) + (c.ativo === false ? ' (desativada)' : '')}
                      </option>
                    ))}
                </select>
              </CampoMP>
              {Number(form.descontos) > 0 && (
                <CampoMP label="Motivo do desconto">
                  <input value={form.descontos_motivo}
                    onChange={e => setForm({ ...form, descontos_motivo: e.target.value })}
                    placeholder="ex: glosa da medição / saldo de crédito do cliente"
                    style={inputMP} />
                </CampoMP>
              )}
              <div style={{ display: 'grid',
                gridTemplateColumns: escNfNumero ? '1fr' : '1fr 1fr', gap: 10 }}>
                <CampoMP label="Status">
                  <select value={form.status}
                    onChange={e => setForm({ ...form, status: e.target.value })}
                    style={inputMP}>
                    <option value="pendente">Pendente</option>
                    <option value="faturada">Faturada</option>
                    <option value="parcialmente_recebida">Parcialmente recebida</option>
                    <option value="recebida">Recebida</option>
                    <option value="atrasada">Atrasada</option>
                    <option value="cancelada">Cancelada</option>
                  </select>
                  {statusAutoAjustado && (
                    <div style={{ fontSize: 10, color: '#0930D0', marginTop: 4 }}>
                      ajustado automaticamente: vencimento no futuro — reverta se for o caso
                    </div>
                  )}
                  {(form.status === 'pendente' || form.status === 'faturada')
                    && !!form.data_vencimento && form.data_vencimento < hojeIso() && (
                    <div style={{ fontSize: 10, color: '#B07500', marginTop: 4 }}>
                      vencida — aparece como Atrasada na lista
                    </div>
                  )}
                </CampoMP>
                {!escNfNumero && (
                <CampoMP label="Nota fiscal nº">
                  <input value={form.nota_fiscal_numero}
                    onChange={e => setForm({ ...form, nota_fiscal_numero: e.target.value })}
                    placeholder="ex: 123" style={inputMP}
                    disabled={semNf} />
                  {/* A retenção é presumida sempre que há NF; marcar "Não há"
                      é a única forma de dizer que a parcela não gera imposto
                      (rendimento, reembolso, repasse). O backend classifica
                      pelo texto — o checkbox só escreve o texto canônico. */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 5,
                    marginTop: 4, fontSize: 11, color: '#555', cursor: 'pointer' }}>
                    <input type="checkbox" checked={semNf}
                      onChange={e => setForm({ ...form,
                        nota_fiscal_numero: e.target.checked ? NF_TEXTO_SEM : '' })} />
                    Não há NF <span style={{ color: '#999' }}>(sem retenção de impostos)</span>
                  </label>
                </CampoMP>
                )}
              </div>
              {/* Corpo da NF: só na aba Dados pra NF desde 2026-09-01 (era o
                  MESMO campo pintado duas vezes). E-mail de faturamento saiu da
                  tela: a lista de destinatários da NF (aba Dados pra NF) manda. */}

              {/* Sprint F8.6 — Preview da transição automática status → faturada */}
              {form.nota_fiscal_numero.trim() !== ''
                && !(parcela.nota_fiscal_numero || '').trim()
                && ['pendente', 'atrasada'].includes(form.status) && (
                <div style={{
                  padding: '8px 12px', background: '#FFF8E8', border: '0.5px solid #E0B84A',
                  borderRadius: 5, fontSize: 11, color: '#7C5800', display: 'flex',
                  alignItems: 'center', gap: 8,
                }}>
                  <span style={{ fontSize: 14 }}>💡</span>
                  <span>
                    Ao salvar com a NF preenchida, o status vai mudar automaticamente para
                    <strong> "Faturada"</strong>.
                  </span>
                </div>
              )}
            </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
              {/* Sprint F5 — Rateio por setor (% canônico + R$ derivado)
                  FZ 0.3 — agora editável (parcelaId + onMudou) */}
              {!escRateio && (
              <RateioParcela
                rateio={detalhe?.rateio_setores ?? parcela.rateio_setores}
                valorPrevisto={form.valor_previsto}
                parcelaId={parcela.id}
                pct={rateioPct}
                onPct={setRateioPct}
              />
              )}

              {/* Aprovação gerente (só pra parcelas pendentes ainda não aprovadas) */}
              {parcela.status === 'pendente' && !(parcela as any).aprovada_gerente_em && (
                <div style={{ padding: 12, background: '#FAFBFF',
                  border: '0.5px solid #d8dcec', borderRadius: 5,
                  display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, color: '#020D7F', fontWeight: 600 }}>
                    Aguardando aprovação do gerente do contrato:
                  </span>
                  <button onClick={aprovarGerente} disabled={salvando}
                    style={{ padding: '6px 14px', fontSize: 11, fontWeight: 600,
                      background: '#0a7f4a', color: '#fff', border: 'none',
                      borderRadius: 4, cursor: 'pointer' }}>
                    ✓ Aprovar
                  </button>
                  <button onClick={recusarGerente} disabled={salvando}
                    style={{ padding: '6px 14px', fontSize: 11, fontWeight: 600,
                      background: 'transparent', color: '#B91626',
                      border: '0.5px solid #B91626', borderRadius: 4, cursor: 'pointer' }}>
                    ✕ Recusar
                  </button>
                </div>
              )}
            </div>
          </>)}

          {/* NF-CNAE (2026-07-27, pedido da Marina): tudo que precisa pra emitir
              a NF num lugar só — dados cadastrais do cliente (com copiar),
              código de serviço (editável, herdado do serviço-fim) e descrição. */}
          {aba === 'nf' && !escAbaNf && (() => {
            const linhaNf = (rotulo: string, valor?: string | null) => (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5,
                padding: '5px 0', borderBottom: '0.5px solid #f0f0f0' }}>
                <span style={{ color: '#888', width: 130, flexShrink: 0 }}>{rotulo}</span>
                <span style={{ flex: 1, color: valor ? '#222' : '#bbb' }}>{valor || '—'}</span>
                {valor && (
                  <button type="button" title="Copiar"
                    onClick={() => navigator.clipboard?.writeText(valor)}
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer',
                      fontSize: 13, color: '#0930D0' }}>⧉</button>
                )}
              </div>
            );
            const endCompleto = [detalhe?.cliente_endereco, detalhe?.cliente_bairro,
              [detalhe?.cliente_cidade, detalhe?.cliente_uf].filter(Boolean).join('/'),
              detalhe?.cliente_cep ? `CEP ${detalhe.cliente_cep}` : null].filter(Boolean).join(' · ');
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {/* Quem recebe a NF, no TOPO e sempre à vista (21/09/2026 —
                    antes ficava no fim da aba, com botão de salvar próprio).
                    Grava no Salvar alterações do rodapé, junto com o resto. */}
                <div style={{ paddingBottom: 12, borderBottom: '0.5px solid #e6e8f0' }}>
                  <EmailsNfParcela
                    clienteId={clienteNfId}
                    contratanteId={contratoVinculado?.cliente_id ?? (parcela as any).contratante_id ?? null}
                    clienteNome={clienteNfNome}
                    valor={destinatarios}
                    sugerido={destSugerido}
                    legado={destLegado}
                    alterado={destAlterado}
                    estadoDetalhe={detalheStatus}
                    onChange={mudarDestinatarios}
                    digitado={destDigitado}
                    onDigitado={setDestDigitado}
                    onContatos={(cs) => { contatosNfRef.current = cs; }}
                  />
                </div>
                {/* 2 colunas (auto-fit): tomador+valores | CNAE+corpo. */}
                <div style={{ display: 'grid', gap: '14px 24px', alignItems: 'start',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ padding: 12, background: '#F8F9FB',
                  border: '0.5px solid #d8dcec', borderRadius: 6 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#020D7F', marginBottom: 6 }}>
                    Tomador (cliente)
                    {detalhe?.cliente_retentor_iss && (
                      <span style={{ marginLeft: 8, fontSize: 10.5, color: '#B91626',
                        background: '#FDECEC', padding: '1px 8px', borderRadius: 10 }}>retém ISS</span>
                    )}
                  </div>
                  {!detalhe?.cliente_cnpj && !detalhe?.cliente_endereco && (
                    <div style={{ fontSize: 11.5, color: '#b06a00', marginBottom: 6 }}>
                      Cadastro incompleto — complete em Comercial → Clientes (busca por CNPJ preenche tudo).
                    </div>
                  )}
                  {linhaNf('Razão social', parcela.cliente_nome)}
                  {linhaNf('CNPJ', detalhe?.cliente_cnpj)}
                  {linhaNf('Endereço', endCompleto)}
                  {/* Só o cadastro do cliente — quem RECEBE a NF é a lista do
                      topo da aba (o campo legado email_faturamento aparece lá
                      como sugestão). */}
                  {linhaNf('E-mail (cadastro)', detalhe?.cliente_email)}
                  {linhaNf('Fone', detalhe?.cliente_fone)}
                </div>
                <div style={{ fontSize: 11.5, color: '#888' }}>
                  Valor da parcela: <b style={{ color: '#222' }}>{fmt(parcela.valor_previsto)}</b>
                  {detalhe?.valor_liquido_previsto != null &&
                    <> · líquido previsto <b style={{ color: '#222' }}>{fmt(Number(detalhe.valor_liquido_previsto))}</b></>}
                  {' '}· Tudo nesta aba grava no Salvar alterações (rodapé).
                </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <CampoMP label="Código de serviço da NF (CNAE)">
                  <select value={form.cnae || ''}
                    onChange={e => setForm({ ...form, cnae: e.target.value })}
                    style={{ width: '100%', padding: '7px 8px', fontSize: 13,
                      border: '0.5px solid #ccc', borderRadius: 5 }}>
                    <option value="">— sem código —</option>
                    {CNAES.map(c => <option key={c.codigo} value={c.codigo}>{rotuloCnae(c)}</option>)}
                  </select>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 3 }}>
                    Herdado do serviço-fim do contrato (o produto que se sobressai). 1 código por NF.
                  </div>
                </CampoMP>

                <CampoMP label="Descrição da NF (corpo)">
                  <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                    <textarea rows={3}
                      value={form.descricao_nf || corpoNfDerivado}
                      onChange={e => { camposMexidos.current.add('descricao_nf');
                        setForm({ ...form, descricao_nf: e.target.value }); }}
                      style={{ flex: 1, padding: '7px 8px', fontSize: 13,
                        border: '0.5px solid #ccc', borderRadius: 5, resize: 'vertical' }} />
                    <button type="button" title="Copiar descrição"
                      onClick={() => navigator.clipboard?.writeText(form.descricao_nf || corpoNfDerivado)}
                      style={{ border: '0.5px solid #ccc', background: '#fff', borderRadius: 5,
                        padding: '6px 10px', cursor: 'pointer', fontSize: 13, color: '#0930D0' }}>⧉</button>
                  </div>
                </CampoMP>
                </div>
                </div>

              </div>
            );
          })()}

          {aba === 'pagamento' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Sprint F7 — Sumário de valor líquido (sempre visível) */}
              {calc && (
                <div style={{ padding: 12, background: '#F8F9FB',
                  border: '0.5px solid #d8dcec', borderRadius: 6, fontSize: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ color: '#666' }}>Valor previsto (bruto)</span>
                    <strong>{fmt(calc.valor_previsto)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 11, color: '#888' }}>
                    <span>− Retenções federais ({(calc.federal_pct * 100).toFixed(2)}% · IR+CSLL+PIS+COFINS)</span>
                    <span>−{fmt(calc.valor_previsto * calc.federal_pct)}</span>
                  </div>
                  {calc.iss_pct > 0 ? (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 11, color: '#888' }}>
                      <span>− ISS ({(calc.iss_pct * 100).toFixed(2)}% · cliente retém)</span>
                      <span>−{fmt(calc.valor_previsto * calc.iss_pct)}</span>
                    </div>
                  ) : (
                    <div style={{ marginBottom: 4, fontSize: 10, color: '#aaa', fontStyle: 'italic' }}>
                      Cliente não retém ISS — Hydrodata recolhe depois.
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between',
                    paddingTop: 6, borderTop: '0.5px solid #d8dcec', fontWeight: 600 }}>
                    <span style={{ color: '#0930D0' }}>= Líquido a receber</span>
                    <strong style={{ color: '#0930D0' }}>{fmt(calc.liquido)}</strong>
                  </div>
                  {/* Barra de progresso de recebimento.
                      calc.valor_recebido vem da COLUNA PRÓPRIA da parcela — em BM
                      aglutinada (Profill) o recebido vive nas entradas-filhas, então
                      a coluna é 0 e a barra sumia. Usa a soma viva das entradas
                      (totalEntradas), que recarrega a cada entrada registrada, pra
                      refletir na hora. Fallback à coluna própria quando não há filhas. */}
                  {(() => {
                    const recebidoVivo = entradas.length ? totalEntradas : (calc.valor_recebido || 0);
                    if (recebidoVivo <= 0) return null;
                    const pct = calc.liquido > 0 ? (recebidoVivo / calc.liquido) * 100 : 0;
                    return (
                      <div style={{ marginTop: 8 }}>
                        <div style={{ height: 6, background: '#e0e6ee', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{
                            height: '100%', background: '#0a7f4a',
                            width: `${Math.min(100, pct)}%`,
                          }} />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 10, color: '#666' }}>
                          <span>Já recebido: <strong>{fmt(recebidoVivo)}</strong> ({pct.toFixed(1)}%)</span>
                          <span>Falta: <strong>{fmt(Math.max(0, calc.liquido - recebidoVivo))}</strong></span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Entradas da parcela — recebimentos que compõem o recebido
                  efetivo (BM agrega N pagamentos; pagamento parcial = N entradas). */}
              {(entradas.length > 0 || !parcela.parcela_pai_id) && (
                <div style={{ padding: 12, background: '#fff',
                  border: '0.5px solid #d8dcec', borderRadius: 6, fontSize: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', marginBottom: entradas.length ? 8 : 0 }}>
                    <strong style={{ fontSize: 12, color: '#31356E' }}>
                      📥 Entradas desta parcela {entradas.length > 0 && `(${entradas.length})`}
                    </strong>
                    {!novaEntrada.aberto && (
                      <button onClick={() => setNovaEntrada(n => ({ ...n, aberto: true }))}
                        style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600,
                          background: '#F4F7FF', color: '#0930D0',
                          border: '0.5px solid #C4D2FF', borderRadius: 4, cursor: 'pointer' }}>
                        + Registrar entrada
                      </button>
                    )}
                  </div>
                  {entradas.length === 0 && !novaEntrada.aberto && (
                    <div style={{ fontSize: 11, color: '#999', marginTop: 6 }}>
                      Nenhuma entrada registrada dentro desta parcela.
                      {num(parcela.valor_recebido) > 0 &&
                        ' O recebido atual veio de baixa direta — a primeira entrada registrada o consolida como entrada histórica.'}
                    </div>
                  )}
                  {entradas.length > 0 && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                      <thead>
                        <tr style={{ color: '#888', textAlign: 'left' }}>
                          <th style={{ padding: '3px 6px' }}>Data</th>
                          <th style={{ padding: '3px 6px' }}>Competência</th>
                          <th style={{ padding: '3px 6px', textAlign: 'right' }}>Valor</th>
                          <th style={{ padding: '3px 6px' }}>Origem</th>
                          <th style={{ padding: '3px 6px' }}>Banco</th>
                          <th style={{ padding: '3px 6px' }}>Descrição</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {entradas.map(e => (
                          <tr key={e.id} style={{ borderTop: '0.5px solid #eef0f6' }}>
                            <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>
                              {editandoEntrada?.id === e.id ? (
                                <input type="date" value={editandoEntrada.data}
                                  onChange={ev => setEditandoEntrada({ ...editandoEntrada, data: ev.target.value })}
                                  onKeyDown={ev => {
                                    if (ev.key === 'Enter') salvarEntradaEditada();
                                    if (ev.key === 'Escape') setEditandoEntrada(null);
                                  }}
                                  style={{ padding: '2px 4px', fontSize: 11,
                                    border: '1px solid #0930D0', borderRadius: 3 }} />
                              ) : e.data_recebimento ? fmtData(e.data_recebimento) : '—'}
                            </td>
                            <td style={{ padding: '4px 6px', whiteSpace: 'nowrap', color: '#888' }}>
                              {e.data_competencia ? fmtData(e.data_competencia) : '—'}
                            </td>
                            <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 600 }}>
                              {editandoEntrada?.id === e.id ? (
                                <input autoFocus value={editandoEntrada.valor}
                                  onChange={ev => setEditandoEntrada({ ...editandoEntrada, valor: ev.target.value })}
                                  onKeyDown={ev => {
                                    if (ev.key === 'Enter') salvarEntradaEditada();
                                    if (ev.key === 'Escape') setEditandoEntrada(null);
                                  }}
                                  style={{ width: 96, padding: '2px 5px', fontSize: 11,
                                    textAlign: 'right', border: '1px solid #0930D0', borderRadius: 3 }} />
                              ) : num(e.valor_recebido) > 0
                                ? fmt(num(e.valor_recebido))
                                : <span style={{ color: '#B07500', fontWeight: 500 }}
                                    title="Cobrança lançada, aguardando pagamento">
                                    {fmt(num(e.valor_previsto))} ⏳
                                  </span>}
                            </td>
                            <td style={{ padding: '4px 6px' }}>
                              {e.id_externo_ca || e.origem === 'conta_azul_api'
                                ? <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 8,
                                    background: '#E8F0FE', color: '#0930D0', fontWeight: 600 }}>CA</span>
                                : <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 8,
                                    background: '#F0F1F5', color: '#555', fontWeight: 600 }}>MANUAL</span>}
                            </td>
                            <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }} title={e.qtd_ofx > 0
                              ? `Conciliada com ${e.qtd_ofx} transação(ões) do extrato`
                              : 'Sem transação bancária vinculada'}>
                              {editandoEntrada?.id === e.id ? (
                                <select value={editandoEntrada.conta || ''}
                                  onChange={ev => setEditandoEntrada({ ...editandoEntrada, conta: ev.target.value })}
                                  style={{ padding: '2px 4px', fontSize: 11,
                                    border: '1px solid #0930D0', borderRadius: 3, maxWidth: 150 }}>
                                  <option value="">(sem conta)</option>
                                  {contasFinanceiras.map((c: any) => (
                                    <option key={c.id} value={c.id}>{c.apelido || c.nome}</option>
                                  ))}
                                </select>
                              ) : (<>
                                {e.qtd_ofx > 0 && <span style={{ color: '#0a7f4a', fontWeight: 700 }}>✓ </span>}
                                {e.conta_financeira_apelido
                                  ? <span style={{ color: '#555' }}>{e.conta_financeira_apelido}</span>
                                  : e.qtd_ofx > 0 ? null
                                    : <span style={{ color: '#B07500', fontWeight: 600 }}
                                        title="Sem conta a entrada não aparece no fluxo por conta — clique no lápis pra definir">
                                        sem conta ⚠
                                      </span>}
                              </>)}
                            </td>
                            <td style={{ padding: '4px 6px', color: '#666', maxWidth: 220,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              title={e.descricao || ''}>
                              {e.descricao || '—'}
                            </td>
                            <td style={{ padding: '4px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                              {editandoEntrada?.id === e.id ? (
                                <>
                                  <button onClick={salvarEntradaEditada} disabled={salvando}
                                    title="Salvar ESTA entrada (Enter) — o rodapé salva a parcela"
                                    style={{ border: 'none', background: 'transparent',
                                      cursor: 'pointer', color: '#0a7f4a', fontSize: 12 }}>✓</button>
                                  <button onClick={() => setEditandoEntrada(null)} disabled={salvando}
                                    title="Cancelar (Esc)"
                                    style={{ border: 'none', background: 'transparent',
                                      cursor: 'pointer', color: '#999', fontSize: 12 }}>✕</button>
                                </>
                              ) : (
                                <>
                                  {/* Editável mesmo vindo do CA: quando o valor muda lá depois
                                      do sync, é aqui que se acerta — antes só dava criar outra
                                      entrada, e o valor entrava em dobro. */}
                                  <button onClick={() => setEditandoEntrada({
                                      id: e.id, valor: String(num(e.valor_recebido) || ''),
                                      data: String(e.data_recebimento || '').slice(0, 10),
                                      conta: e.conta_financeira_id || '',
                                    })} disabled={salvando}
                                    title="Corrigir a data ou o valor desta entrada"
                                    style={{ border: 'none', background: 'transparent',
                                      cursor: 'pointer', color: '#0930D0', fontSize: 12 }}>✏️</button>
                                  {e.entrada_manual && (
                                    <button onClick={() => excluirEntrada(e.id)} disabled={salvando}
                                      title="Excluir entrada manual"
                                      style={{ border: 'none', background: 'transparent',
                                        cursor: 'pointer', color: '#c33', fontSize: 12 }}>🗑</button>
                                  )}
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                        <tr style={{ borderTop: '1px solid #d8dcec', fontWeight: 700 }}>
                          <td colSpan={2} style={{ padding: '5px 6px', color: '#31356E' }}>
                            Total das entradas (= recebido efetivo)
                          </td>
                          <td style={{ padding: '5px 6px', textAlign: 'right', color: '#0a7f4a' }}>
                            {fmt(totalEntradas)}
                          </td>
                          <td colSpan={4} />
                        </tr>
                      </tbody>
                    </table>
                  )}
                  {novaEntrada.aberto && (
                    <div style={{ marginTop: 10, padding: 10, background: '#F8F9FB',
                      border: '0.5px solid #d8dcec', borderRadius: 6 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <CampoMP label="Valor recebido (R$)">
                          <input type="number" step={0.01} min={0} autoFocus
                            value={novaEntrada.valor}
                            onChange={ev => setNovaEntrada(n => ({ ...n, valor: ev.target.value }))}
                            style={inputMP} />
                        </CampoMP>
                        <CampoMP label="Data da entrada">
                          <input type="date" value={novaEntrada.data}
                            onChange={ev => setNovaEntrada(n => ({ ...n, data: ev.target.value }))}
                            style={inputMP} />
                        </CampoMP>
                      </div>
                      <CampoMP label="Conta financeira (onde o dinheiro entrou)">
                        <select value={novaEntrada.conta_financeira_id}
                          onChange={ev => setNovaEntrada(n => ({ ...n, conta_financeira_id: ev.target.value }))}
                          style={{ ...inputMP, borderColor: novaEntrada.conta_financeira_id ? undefined : '#E0B84A' }}>
                          <option value="">— escolha (sem conta a entrada some do fluxo por conta) —</option>
                          {contasFinanceiras.map((c: any) => (
                            <option key={c.id} value={c.id}>{c.apelido || c.nome}</option>
                          ))}
                        </select>
                      </CampoMP>
                      <CampoMP label="Descrição da entrada (opcional)">
                        <input value={novaEntrada.descricao}
                          placeholder="ex: TED Banco Inter — pagamento parcial"
                          onChange={ev => setNovaEntrada(n => ({ ...n, descricao: ev.target.value }))}
                          style={inputMP} />
                      </CampoMP>
                      <div style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
                        <button onClick={() => setNovaEntrada(n => ({ ...n, aberto: false }))}
                          disabled={salvando}
                          style={{ padding: '5px 12px', fontSize: 11, background: '#fff',
                            border: '0.5px solid #ccc', borderRadius: 4, cursor: 'pointer' }}>
                          Cancelar
                        </button>
                        <button onClick={registrarEntrada} disabled={salvando}
                          style={{ padding: '5px 12px', fontSize: 11, fontWeight: 600,
                            background: '#0930D0', color: '#fff', border: 'none',
                            borderRadius: 4, cursor: 'pointer' }}>
                          {salvando ? 'Registrando…' : 'Registrar entrada'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Recebida via Conta Azul mas SEM OFX local — caso comum quando
                  o CA sync trouxe o status mas o extrato OFX ainda nao foi
                  conciliado pra ela. Oferece atalho pra procurar OFX equivalente. */}
              {num(parcela.qtd_ofx_vinculadas) === 0
                && parcela.status === 'recebida'
                && num(parcela.valor_recebido) > 0
                && parcela.id_externo_ca && (
                <div style={{ padding: 12, background: '#FFF8E8',
                  border: '0.5px solid #E0B84A', borderRadius: 6, fontSize: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <div>
                      <strong style={{ color: '#7C5800', fontSize: 12 }}>
                        ⚠ Recebida via Conta Azul · sem OFX local vinculado
                      </strong>
                      <div style={{ fontSize: 11, color: '#7C5800', marginTop: 2 }}>
                        Valor R$ {num(parcela.valor_recebido).toFixed(2)}
                        {parcela.data_recebimento_real && ` em ${fmtData(parcela.data_recebimento_real)}`}
                        {parcela.conta_financeira_ca && ` · ${parcela.conta_financeira_ca}`}
                        <br />
                        A conciliação CA não criou vínculo com transação OFX.
                        Procure o depósito no extrato pra vincular.
                      </div>
                    </div>
                    <a href="#" onClick={(e) => {
                      e.preventDefault();
                      irParaExtrato(num(parcela.valor_recebido));
                    }} style={{ color: '#7C5800', fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap' }}>
                      🔍 Buscar OFX correspondente →
                    </a>
                  </div>
                </div>
              )}

              {/* Conciliação OFX (banco) — lista de transações vinculadas
                  via conciliação no extrato. Mostra saldo a receber e oferece
                  atalho pra buscar o depósito remanescente. */}
              {(num(parcela.qtd_ofx_vinculadas) > 0 || num(parcela.soma_ofx_vinculada) > 0) && (
                <div style={{ padding: 12, background: '#EFF3FF',
                  border: '0.5px solid #B7C5E8', borderRadius: 6, fontSize: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <strong style={{ color: '#0930D0', fontSize: 12 }}>
                      🔗 Conciliação bancária ({num(parcela.qtd_ofx_vinculadas)} {num(parcela.qtd_ofx_vinculadas) !== 1 ? 'transações' : 'transação'} OFX)
                    </strong>
                    <strong style={{ color: '#0930D0' }}>
                      {fmt(num(parcela.soma_ofx_vinculada))}
                    </strong>
                  </div>
                  {/* Compara soma OFX vs líquido pra mostrar saldo */}
                  {(() => {
                    const bruto = num(parcela.valor_previsto);
                    const somaOfx = num(parcela.soma_ofx_vinculada);
                    // FIN-MATCH-LIQ2: líquido A RECEBER = líquido − descontos
                    // (glosa/saldo), piso 0 — o backend manda pronto em
                    // estado.previsto; fallback local enquanto não vier.
                    const liquido = parcela.estado?.previsto?.liquido_a_receber != null
                      ? num(parcela.estado.previsto.liquido_a_receber)
                      : Math.max((num(parcela.valor_liquido_previsto) || num(parcela.valor_previsto))
                          - num(parcela.descontos), 0);
                    const faltaLiquido = liquido - somaOfx;
                    const faltaBruto = bruto - somaOfx;
                    // Considera "fechado" se soma bate com líquido OU bruto (tol R$0,02)
                    const fechouLiq = Math.abs(faltaLiquido) <= 0.02;
                    const fechouBruto = Math.abs(faltaBruto) <= 0.02;
                    const fechou = fechouLiq || fechouBruto;
                    return (
                      <div style={{ fontSize: 11, color: fechou ? '#0a7f4a' : '#B07500',
                        marginTop: 4, paddingTop: 6, borderTop: '0.5px dashed #B7C5E8' }}>
                        {fechou ? (
                          <span>✓ Soma OFX bate com valor {fechouLiq ? 'líquido' : 'bruto'} — parcela quitada via banco.</span>
                        ) : faltaLiquido > 0 ? (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                            <span>
                              ⚠ Saldo a receber: <strong>{fmt(faltaLiquido)}</strong>{' '}
                              <span style={{ color: '#888' }}>(comparando com líquido {fmt(liquido)})</span>
                            </span>
                            <a href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                irParaExtrato(faltaLiquido);
                              }}
                              style={{ color: '#0930D0', fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap', cursor: 'pointer' }}>
                              🔍 Buscar no extrato →
                            </a>
                          </div>
                        ) : (
                          <span>⚠ OFX ultrapassou o valor previsto em {fmt(Math.abs(faltaLiquido))} (vínculo errado?)</span>
                        )}
                      </div>
                    );
                  })()}
                  {/* Lista das OFX vinculadas */}
                  {ofxVinculadas.length > 0 && (
                    <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {ofxVinculadas.map(t => (
                        <div key={t.id} style={{
                          padding: '6px 10px', background: '#fff', borderRadius: 4,
                          fontSize: 11, display: 'flex', justifyContent: 'space-between',
                          gap: 8, alignItems: 'center',
                        }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 10, color: '#888' }}>
                              {fmtData(t.data_transacao)} · {t.banco} {t.conta_id}
                            </div>
                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap', color: '#444' }} title={t.descricao}>
                              {t.descricao}
                            </div>
                          </div>
                          <strong style={{ color: t.valor > 0 ? '#0a7f4a' : '#B91626' }}>
                            {t.valor > 0 ? '+' : ''}{fmt(t.valor)}
                          </strong>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Resumo de quitação — o REGISTRO/ajuste de recebimento vive no
                  card "Entradas desta parcela" acima (form antigo aposentado
                  2026-07-08: era redundante com as entradas). */}
              {parcela.status === 'recebida' && (
                <div style={{ padding: '10px 14px', background: '#E8F5EE',
                  border: '0.5px solid #B5D6BF', borderRadius: 6 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#0a7f4a' }}>
                    ✓ Quitada em {fmtData(parcela.data_recebimento)}
                  </div>
                  <div style={{ fontSize: 11, color: '#0a7f4a', marginTop: 4 }}>
                    Total recebido: <strong>
                      {fmt(entradas.length ? totalEntradas : num(parcela.valor_recebido))}
                    </strong>
                    {parcela.meio_pagamento && ` · último meio: ${parcela.meio_pagamento}`}
                    {(() => {  // FZ 5.2 — conta do recebimento (da parcela ou da 1ª entrada com conta)
                      const conta = parcela.conta_financeira_apelido
                        || entradas.find(e => e.conta_financeira_apelido)?.conta_financeira_apelido;
                      return conta ? ` · conta: ${conta}` : null;
                    })()}
                    {' '}— pra ajustar, gerencie as entradas acima.
                  </div>
                </div>
              )}
            </div>
          )}

          {aba === 'anexos' && <AnexosParcela parcelaId={parcela.id} />}
          {aba === 'historico' && <HistoricoParcela parcelaId={parcela.id} onMudou={onMudou} />}
        </div>

        {/* Rodapé em TODAS as abas (30/09/2026, Lucas: "o botão salvar deve
            servir para todas as alterações realizadas na parcela"). Antes ele
            sumia em Pagamento/Anexos/Histórico: quem tinha editado os Detalhes
            e trocava de aba ficava sem onde salvar — e fechava perdendo tudo.
            O formulário inteiro (Detalhes + Dados pra NF + rateio +
            destinatários) vai num POST só. */}
        <div style={{ padding: '12px 20px', borderTop: '0.5px solid #e0e0e0',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            {/* Sprint F4: Excluir à esquerda (operação destrutiva visualmente isolada) */}
            <button onClick={excluirParcela} disabled={salvando}
              title="Excluir parcela (pede confirmação por texto)"
              style={{ padding: '8px 14px', fontSize: 12, background: 'transparent',
                color: '#B91626', border: '0.5px solid #FBBABA', borderRadius: 4,
                cursor: salvando ? 'wait' : 'pointer' }}>
              🗑 Excluir parcela
            </button>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {/* Save-gate: a listagem trunca texto em 200 chars; salvar antes
                  do detalhe chegar gravaria o corte. Falha de rede não tranca
                  o modal — reabilita com aviso + tentar de novo. */}
              {detalheStatus === 'falhou' && (
                <span style={{ fontSize: 10.5, color: '#B07500', maxWidth: 420, textAlign: 'right' }}>
                  ⚠ O detalhe completo não carregou — texto longo pode ser salvo cortado.{' '}
                  <button type="button" onClick={() => carregarDetalhe(true)}
                    style={{ background: 'transparent', border: 'none', color: '#0930D0',
                      cursor: 'pointer', fontSize: 10.5, fontWeight: 600, padding: 0,
                      textDecoration: 'underline' }}>
                    tentar de novo
                  </button>
                </span>
              )}
              {pendencias.length > 0 && !salvando && (
                <span style={{ fontSize: 11, color: '#B07500', fontWeight: 600 }}
                  title={`Mexido e não salvo: ${pendencias.join(', ')}`}>
                  ● alterações não salvas
                </span>
              )}
              <button onClick={fecharModal} disabled={salvando}
                style={{ padding: '8px 16px', fontSize: 12, background: '#fff',
                  color: '#555', border: '0.5px solid #ddd', borderRadius: 4,
                  cursor: salvando ? 'wait' : 'pointer' }}>
                Fechar
              </button>
              <button onClick={salvarDetalhes}
                disabled={salvando || detalheStatus === 'carregando'}
                style={{ padding: '8px 20px', fontSize: 12, fontWeight: 600,
                  background: detalheStatus === 'carregando' ? '#8FA0E8' : '#0930D0',
                  color: '#fff', border: 'none',
                  borderRadius: 4, cursor: (salvando || detalheStatus === 'carregando') ? 'wait' : 'pointer' }}>
                {salvando ? 'Salvando…'
                  : detalheStatus === 'carregando' ? 'Carregando dados…'
                  : 'Salvar alterações'}
              </button>
          </div>
        </div>
      </div>
      {/* F-CR-merge: modal de mesclar parcela CA órfã com parcela do sistema */}
      {mostrarMerge && (
        <ModalMesclarParcelaCr parcela={parcela}
          onFechar={() => setMostrarMerge(false)}
          onMesclou={() => { setMostrarMerge(false); onMudou(); onClose(); }} />
      )}
    </div>,
    document.body
  );
}
